import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Language = 'ar' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isRTL: boolean;
  t: (key: string) => string;
}

const translations: Record<Language, Record<string, string>> = {
  ar: {
    // Navigation
    'nav.home': 'الرئيسية',
    'nav.about': 'عن الشركة',
    'nav.tenders': 'المناقصات',
    'nav.howItWorks': 'كيف يعمل',
    'nav.portfolio': 'معرض الأعمال',
    'nav.contact': 'تواصل معنا',
    'nav.login': 'تسجيل الدخول',
    'nav.register': 'سجل الآن',
    'nav.profile': 'الملف الشخصي',
    'nav.settings': 'الإعدادات',
    'nav.logout': 'تسجيل الخروج',
    
    // Footer
    'footer.description': 'منصة متكاملة لإدارة المناقصات والمشاريع في قطاع المقاولات والبناء',
    'footer.quickLinks': 'روابط سريعة',
    'footer.legal': 'قانوني',
    'footer.privacy': 'سياسة الخصوصية',
    'footer.terms': 'الشروط والأحكام',
    'footer.contact': 'تواصل معنا',
    'footer.rights': 'جميع الحقوق محفوظة',
    'footer.viewLocation': 'عرض الموقع',
    'footer.noLinks': 'لا توجد روابط',
    
    // Sidebar
    'sidebar.dashboard': 'لوحة البيانات',
    'sidebar.profile': 'الملف الشخصي',
    'sidebar.portfolio': 'معرض الأعمال',
    'sidebar.tenders': 'المناقصات',
    'sidebar.applications': 'طلباتي',
    'sidebar.notifications': 'الرسائل',
    'sidebar.settings': 'الإعدادات',
    'sidebar.clients': 'إدارة العملاء',
    'sidebar.contentManagement': 'إدارة المحتوى',
    'sidebar.logout': 'تسجيل الخروج',
    'sidebar.companyProjects': 'مشاريع الشركة',
    'sidebar.clientProjects': 'مشاريع العملاء',
    'sidebar.adminUsers': 'إدارة المسؤولين',
    
    // Content Management
    'content.title': 'إدارة المحتوى',
    'content.subtitle': 'إدارة محتوى المنصة من الصفحة الرئيسية، من نحن، كيف يعمل، الأسئلة الشائعة، التواصل، الروابط، والتذييل',
    'content.tabs.homepage': 'الصفحة الرئيسية',
    'content.tabs.about': 'من نحن',
    'content.tabs.howItWorks': 'كيف يعمل',
    'content.tabs.faqs': 'الأسئلة الشائعة',
    'content.tabs.contact': 'التواصل',
    'content.tabs.navigation': 'الروابط',
    'content.tabs.footer': 'التذييل',
    
    // Homepage Content
    'content.homepage.title': 'محتوى الصفحة الرئيسية',
    'content.homepage.description': 'إدارة محتوى الصفحة الرئيسية',
    'content.homepage.edit': 'تعديل المحتوى',
    'content.homepage.noContent': 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.',
    'content.homepage.features': 'الميزات',
    'content.homepage.featuresDescription': 'إدارة ميزات الصفحة الرئيسية',
    'content.homepage.addFeature': 'إضافة ميزة',
    'content.homepage.noFeatures': 'لا توجد ميزات. اضغط على إضافة ميزة لإضافة ميزة جديدة.',
    'content.homepage.statistics': 'الإحصائيات',
    'content.homepage.statisticsDescription': 'إدارة إحصائيات الصفحة الرئيسية',
    'content.homepage.addStatistic': 'إضافة إحصائية',
    'content.homepage.noStatistics': 'لا توجد إحصائيات. اضغط على إضافة إحصائية لإضافة إحصائية جديدة.',
    'content.homepage.updated': 'تم تحديث محتوى الصفحة الرئيسية',
    'content.homepage.updatedDesc': 'تم تحديث محتوى الصفحة الرئيسية بنجاح',
    'content.homepage.editDescription': 'قم بتعديل محتوى الصفحة الرئيسية',
    'content.homepage.mainTitle': 'العنوان الرئيسي',
    'content.homepage.mainDescription': 'الوصف الرئيسي',
    'content.homepage.mainTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.homepage.mainTitleAr': 'العنوان الرئيسي (عربي)',
    'content.homepage.mainDescriptionEn': 'الوصف الرئيسي (إنجليزي)',
    'content.homepage.mainDescriptionAr': 'الوصف الرئيسي (عربي)',
    'content.homepage.mainCtaTextEn': 'نص زر الدعوة (إنجليزي)',
    'content.homepage.mainCtaTextAr': 'نص زر الدعوة (عربي)',
    'content.homepage.mainCtaUrl': 'رابط زر الدعوة',
    // Legacy background terms for backward compatibility
    'content.homepage.heroTitle': 'العنوان الرئيسي',
    'content.homepage.heroDescription': 'الوصف الرئيسي',
    'content.homepage.heroTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.homepage.heroTitleAr': 'العنوان الرئيسي (عربي)',
    'content.homepage.heroTitleColor': 'لون عنوان الخلفية',
    'content.homepage.heroDescriptionEn': 'وصف الخلفية (إنجليزي)',
    'content.homepage.heroDescriptionAr': 'الوصف الرئيسي (عربي)',
    'content.homepage.heroCtaTextEn': 'نص زر الدعوة (إنجليزي)',
    'content.homepage.heroCtaTextAr': 'نص زر الدعوة (عربي)',
    'content.homepage.heroCtaText': 'نص زر الدعوة',
    'content.homepage.heroCtaUrl': 'رابط زر الدعوة',
    'content.homepage.sectionSubtitleEn': 'عنوان القسم الفرعي (إنجليزي)',
    'content.homepage.sectionSubtitleAr': 'عنوان القسم الفرعي (عربي)',
    'content.homepage.sectionSubtitle': 'عنوان القسم الفرعي',
    'content.homepage.mainCtaText': 'نص زر الدعوة',
    'content.homepage.mainBackgroundImage': 'صورة الخلفية الرئيسية',
    
    // About Content
    'content.about.title': 'محتوى صفحة من نحن',
    'content.about.description': 'إدارة محتوى صفحة من نحن',
    'content.about.edit': 'تعديل المحتوى',
    'content.about.noContent': 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.',
    'content.about.values': 'القيم',
    'content.about.valuesDescription': 'إدارة قيم الشركة',
    'content.about.addValue': 'إضافة قيمة',
    'content.about.noValues': 'لا توجد قيم. اضغط على إضافة قيمة لإضافة قيمة جديدة.',
    'content.about.updated': 'تم تحديث محتوى صفحة من نحن',
    'content.about.updatedDesc': 'تم تحديث محتوى صفحة من نحن بنجاح',
    'content.about.mainTitle': 'العنوان الرئيسي',
    'content.about.mainTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.about.mainTitleAr': 'العنوان الرئيسي (عربي)',
    'content.about.heroTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.about.heroTitleAr': 'العنوان الرئيسي (عربي)',
    'content.about.mainSubtitleEn': 'الوصف الرئيسي (إنجليزي)',
    'content.about.mainSubtitleAr': 'الوصف الرئيسي (عربي)',
    'content.about.heroSubtitleEn': 'الوصف الرئيسي (إنجليزي)',
    'content.about.heroSubtitleAr': 'الوصف الرئيسي (عربي)',
    'content.about.heroTitle': 'العنوان الرئيسي للخلفية',
    'content.about.heroSubtitle': 'الوصف الرئيسي للخلفية',
    'content.about.visionTitle': 'عنوان الرؤية',
    'content.about.visionTitleEn': 'عنوان الرؤية (إنجليزي)',
    'content.about.visionTitleAr': 'عنوان الرؤية (عربي)',
    'content.about.visionContentEn': 'محتوى الرؤية (إنجليزي)',
    'content.about.visionContentAr': 'محتوى الرؤية (عربي)',
    'content.about.visionIcon': 'أيقونة الرؤية',
    'content.about.missionTitle': 'عنوان المهمة',
    'content.about.missionTitleEn': 'عنوان المهمة (إنجليزي)',
    'content.about.missionTitleAr': 'عنوان المهمة (عربي)',
    'content.about.missionContentEn': 'محتوى المهمة (إنجليزي)',
    'content.about.missionContentAr': 'محتوى المهمة (عربي)',
    'content.about.missionIcon': 'أيقونة المهمة',
    'content.about.valuesSectionTitleEn': 'عنوان قسم القيم (إنجليزي)',
    'content.about.valuesSectionTitleAr': 'عنوان قسم القيم (عربي)',
    'content.about.editDescription': 'قم بتعديل محتوى صفحة من نحن',
    
    'content.value.created': 'تم إنشاء القيمة',
    'content.value.createdDesc': 'تم إنشاء القيمة بنجاح',
    'content.value.updated': 'تم تحديث القيمة',
    'content.value.updatedDesc': 'تم تحديث القيمة بنجاح',
    'content.value.deleted': 'تم حذف القيمة',
    'content.value.deletedDesc': 'تم حذف القيمة بنجاح',
    'content.value.add': 'إضافة قيمة',
    'content.value.edit': 'تعديل القيمة',
    'content.value.order': 'الترتيب',
    'content.value.icon': 'الأيقونة',
    'content.value.titleEn': 'العنوان (إنجليزي)',
    'content.value.titleAr': 'العنوان (عربي)',
    'content.value.contentEn': 'المحتوى (إنجليزي)',
    'content.value.contentAr': 'المحتوى (عربي)',
    'content.value.confirmDelete': 'هل أنت متأكد من حذف هذه القيمة؟',
    'content.value.editDesc': 'قم بتعديل بيانات القيمة',
    'content.value.addDesc': 'قم بإضافة قيمة جديدة',
    'content.value.title': 'العنوان',
    'content.value.content': 'المحتوى',
    'content.value.status': 'الحالة',
    
    // How It Works Content
    'content.howItWorks.title': 'محتوى صفحة كيف يعمل',
    'content.howItWorks.description': 'إدارة محتوى صفحة كيف يعمل',
    'content.howItWorks.edit': 'تعديل المحتوى',
    'content.howItWorks.noContent': 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.',
    'content.howItWorks.steps': 'الخطوات',
    'content.howItWorks.stepsDescription': 'إدارة خطوات كيف يعمل',
    'content.howItWorks.addStep': 'إضافة خطوة',
    'content.howItWorks.noSteps': 'لا توجد خطوات. اضغط على إضافة خطوة لإضافة خطوة جديدة.',
    'content.howItWorks.updated': 'تم تحديث محتوى صفحة كيف يعمل',
    'content.howItWorks.updatedDesc': 'تم تحديث محتوى صفحة كيف يعمل بنجاح',
    'content.howItWorks.mainTitle': 'العنوان الرئيسي',
    'content.howItWorks.mainTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.howItWorks.mainTitleAr': 'العنوان الرئيسي (عربي)',
    'content.howItWorks.mainSubtitle': 'الوصف الرئيسي',
    'content.howItWorks.mainSubtitleEn': 'الوصف الرئيسي (إنجليزي)',
    'content.howItWorks.mainSubtitleAr': 'الوصف الرئيسي (عربي)',
    'content.howItWorks.ctaButtonTextEn': 'نص زر الدعوة (إنجليزي)',
    'content.howItWorks.ctaButtonTextAr': 'نص زر الدعوة (عربي)',
    'content.howItWorks.ctaButtonUrl': 'رابط زر الدعوة',
    'content.howItWorks.heroTitle': 'العنوان الرئيسي للخلفية',
    'content.howItWorks.heroSubtitle': 'الوصف الرئيسي للخلفية',
    'content.howItWorks.ctaButtonText': 'نص زر الدعوة',
    
    // FAQs Content
    'content.faqs.title': 'الأسئلة الشائعة',
    'content.faqs.description': 'إدارة الأسئلة الشائعة',
    'content.faqs.add': 'إضافة سؤال',
    'content.faqs.noFaqs': 'لا توجد أسئلة. اضغط على إضافة سؤال لإضافة سؤال جديد.',
    
    // Contact Content
    'content.contact.title': 'محتوى صفحة التواصل',
    'content.contact.description': 'إدارة محتوى صفحة التواصل',
    'content.contact.edit': 'تعديل المحتوى',
    'content.contact.noContent': 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.',
    'content.contact.updated': 'تم تحديث محتوى صفحة التواصل',
    'content.contact.updatedDesc': 'تم تحديث محتوى صفحة التواصل بنجاح',
    'content.contact.mainTitle': 'العنوان الرئيسي',
    'content.contact.mainTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.contact.mainTitleAr': 'العنوان الرئيسي (عربي)',
    'content.contact.heroTitleEn': 'العنوان الرئيسي (إنجليزي)',
    'content.contact.heroTitleAr': 'العنوان الرئيسي (عربي)',
    'content.contact.mainSubtitleEn': 'الوصف الرئيسي (إنجليزي)',
    'content.contact.mainSubtitleAr': 'الوصف الرئيسي (عربي)',
    'content.contact.heroSubtitleEn': 'الوصف الرئيسي (إنجليزي)',
    'content.contact.heroSubtitleAr': 'الوصف الرئيسي (عربي)',
    'content.contact.heroTitle': 'العنوان الرئيسي للخلفية',
    'content.contact.phone': 'الهاتف',
    'content.contact.email': 'البريد الإلكتروني',
    'content.contact.addressEn': 'العنوان (إنجليزي)',
    'content.contact.addressAr': 'العنوان (عربي)',
    'content.contact.formTitleEn': 'عنوان النموذج (إنجليزي)',
    'content.contact.formTitleAr': 'عنوان النموذج (عربي)',
    'content.contact.formNameLabelEn': 'تسمية الاسم (إنجليزي)',
    'content.contact.formNameLabelAr': 'تسمية الاسم (عربي)',
    'content.contact.formEmailLabelEn': 'تسمية البريد (إنجليزي)',
    'content.contact.formEmailLabelAr': 'تسمية البريد (عربي)',
    'content.contact.formPhoneLabelEn': 'تسمية الهاتف (إنجليزي)',
    'content.contact.formPhoneLabelAr': 'تسمية الهاتف (عربي)',
    'content.contact.formSubjectLabelEn': 'تسمية الموضوع (إنجليزي)',
    'content.contact.formSubjectLabelAr': 'تسمية الموضوع (عربي)',
    'content.contact.formMessageLabelEn': 'تسمية الرسالة (إنجليزي)',
    'content.contact.formMessageLabelAr': 'تسمية الرسالة (عربي)',
    'content.contact.formSubmitButtonTextEn': 'نص زر الإرسال (إنجليزي)',
    'content.contact.formSubmitButtonTextAr': 'نص زر الإرسال (عربي)',
    'content.contact.editDescription': 'قم بتعديل محتوى صفحة التواصل',
    'content.contact.phoneIcon': 'أيقونة الهاتف',
    'content.contact.emailIcon': 'أيقونة البريد',
    'content.contact.addressIcon': 'أيقونة العنوان',
    
    // Navigation Content
    'content.navigation.title': 'روابط التنقل',
    'content.navigation.description': 'إدارة روابط القائمة الرئيسية',
    'content.navigation.add': 'إضافة رابط',
    'content.navigation.noLinks': 'لا توجد روابط. اضغط على إضافة رابط لإضافة رابط جديد.',
    
    // Footer Content
    'content.footer.title': 'محتوى التذييل',
    'content.footer.description': 'إدارة محتوى التذييل',
    'content.footer.edit': 'تعديل المحتوى',
    'content.footer.noContent': 'لا يوجد محتوى. اضغط على تعديل المحتوى لإضافة محتوى جديد.',
    'content.footer.updated': 'تم تحديث محتوى التذييل',
    'content.footer.updatedDesc': 'تم تحديث محتوى التذييل بنجاح',
    'content.footer.companyDescription': 'وصف الشركة',
    'content.footer.companyDescriptionEn': 'وصف الشركة (إنجليزي)',
    'content.footer.companyDescriptionAr': 'وصف الشركة (عربي)',
    'content.footer.phone': 'الهاتف',
    'content.footer.email': 'البريد الإلكتروني',
    'content.footer.copyrightText': 'نص حقوق النشر',
    'content.footer.quickLinksTitleEn': 'عنوان الروابط السريعة (إنجليزي)',
    'content.footer.quickLinksTitleAr': 'عنوان الروابط السريعة (عربي)',
    'content.footer.legalTitleEn': 'عنوان القانوني (إنجليزي)',
    'content.footer.legalTitleAr': 'عنوان القانوني (عربي)',
    'content.footer.contactTitleEn': 'عنوان التواصل (إنجليزي)',
    'content.footer.contactTitleAr': 'عنوان التواصل (عربي)',
    'content.footer.locationEn': 'الموقع (إنجليزي)',
    'content.footer.locationAr': 'الموقع (عربي)',
    'content.footer.companyLogo': 'شعار الشركة',
    'content.footer.quickLinksTitle': 'عنوان الروابط السريعة',
    'content.footer.legalTitle': 'عنوان القانونية',
    'content.footer.contactTitle': 'عنوان التواصل',
    'content.footer.location': 'الموقع',
    'content.footer.status': 'الحالة',
    'content.footer.createdAt': 'تاريخ الإنشاء',
    'content.footer.updatedAt': 'تاريخ التحديث',
    'content.footer.logoHint': 'اختر صورة الشعار (PNG, JPG, WEBP)',
    
    // Site Settings Notifications
    'settings.notifications.updated': 'تم تحديث الإعدادات',
    'settings.notifications.updatedDesc': 'تم تحديث إعدادات الموقع بنجاح.',
    'settings.notifications.updateFailed': 'فشل تحديث الإعدادات.',
    'settings.notifications.logoUploaded': 'تم رفع الشعار',
    'settings.notifications.logoUploadedDesc': 'تم رفع الشعار بنجاح.',
    'settings.notifications.logoUploadFailed': 'فشل رفع الشعار.',
    'settings.notifications.darkLogoUploaded': 'تم رفع الشعار الداكن',
    'settings.notifications.darkLogoUploadedDesc': 'تم رفع الشعار الداكن بنجاح.',
    'settings.notifications.darkLogoUploadFailed': 'فشل رفع الشعار الداكن.',
    'settings.notifications.faviconUploaded': 'تم رفع الأيقونة',
    'settings.notifications.faviconUploadedDesc': 'تم رفع أيقونة الموقع بنجاح.',
    'settings.notifications.faviconUploadFailed': 'فشل رفع الأيقونة.',
    'settings.notifications.logoDeleted': 'تم حذف الشعار',
    'settings.notifications.logoDeletedDesc': 'تم حذف الشعار بنجاح.',
    'settings.notifications.logoDeleteFailed': 'فشل حذف الشعار.',
    
    // Tenders Notifications
    'tenders.notifications.offerSubmitted': 'تم تقديم العرض بنجاح',
    'tenders.notifications.offerSubmittedDesc': 'سيتم مراجعة عرضك والتواصل معك قريباً',
    'tenders.notifications.offerFailed': 'فشل تقديم العرض',
    'tenders.notifications.offerFailedDesc': 'حدث خطأ غير متوقع',
    
    // Profile Notifications
    'profile.notifications.updated': 'تم تحديث الملف الشخصي',
    'profile.notifications.updatedDesc': 'تم حفظ التغييرات بنجاح',
    'profile.notifications.updateFailed': 'فشل التحديث',
    'profile.notifications.updateFailedDesc': 'حدث خطأ غير متوقع',
    'profile.notifications.documentsUploaded': 'تم رفع المستندات',
    'profile.notifications.documentsUploadedDesc': 'تم رفع المستندات بنجاح',
    'profile.notifications.documentsUploadFailed': 'فشل رفع المستندات',
    'profile.notifications.documentsUploadFailedDesc': 'حدث خطأ غير متوقع',
    'profile.notifications.documentDeleted': 'تم حذف المستند',
    'profile.notifications.documentDeletedDesc': 'تم حذف المستند بنجاح',
    'profile.notifications.documentDeleteFailed': 'فشل حذف المستند',
    'profile.notifications.documentDeleteFailedDesc': 'حدث خطأ غير متوقع',
    'profile.notifications.imageUpdated': 'تم تحديث الملف الشخصي',
    'profile.notifications.imageUpdatedDesc': 'تم تحديث الملف الشخصي بنجاح',
    'profile.notifications.imageUpdateFailed': 'فشل تحديث الملف الشخصي',
    'profile.notifications.imageUpdateFailedDesc': 'حدث خطأ غير متوقع',
    
    // Site Settings
    'settings.siteSettings': 'إعدادات الموقع',
    'settings.siteSettingsDesc': 'قم بتكوين اسم الموقع وشعاره',
    'settings.siteName': 'اسم الموقع',
    'settings.siteNameEn': 'اسم الموقع (إنجليزي)',
    'settings.siteNameAr': 'اسم الموقع (عربي)',
    'settings.saveSiteSettings': 'حفظ إعدادات الموقع',
    'settings.siteLogo.title': 'شعار الموقع',
    'settings.siteLogo.description': 'رفع وإدارة شعار الموقع',
    'settings.siteLogo.recommended': 'موصى به: PNG أو SVG بخلفية شفافة. الحد الأقصى 2 ميجابايت.',
    'settings.siteLogo.alt': 'شعار الموقع',
    'settings.heroBackground.title': 'صورة الخلفية',
    'content.homepage.heroBackgroundImage': 'صورة الخلفية',
    'settings.heroBackground.description': 'رفع صورة خلفية للقسم الرئيسي في الصفحة الرئيسية',
    'settings.heroBackground.recommended': 'موصى به: صورة عالية الجودة (1920×1080 أو أعلى). الحد الأقصى 5 ميجابايت. يدعم PNG, JPG, WebP.',
    'settings.heroBackground.alt': 'صورة الخلفية',
    'settings.upload': 'رفع',
    'settings.uploading': 'جاري الرفع...',
    'settings.uploadSuccess': 'تم الرفع بنجاح',
    'settings.uploadError': 'فشل في الرفع',
    'settings.deleteSuccess': 'تم الحذف بنجاح',
    'settings.deleteError': 'فشل في الحذف',
    'settings.heroBackground.uploadSuccess': 'تم رفع صورة الخلفية بنجاح',
    'settings.heroBackground.uploadError': 'فشل في رفع صورة الخلفية',
    'settings.heroBackground.deleteSuccess': 'تم حذف صورة الخلفية بنجاح',
    'settings.heroBackground.deleteError': 'فشل في حذف صورة الخلفية',
    'settings.logo.uploadSuccess': 'تم رفع الشعار بنجاح',
    'settings.logo.uploadError': 'فشل في رفع الشعار',
    'settings.logo.deleteSuccess': 'تم حذف الشعار بنجاح',
    'settings.logo.deleteError': 'فشل في حذف الشعار',
    
    // Feature/Value/Step/FAQ CRUD messages
    'content.feature.created': 'تم إنشاء الميزة',
    'content.feature.createdDesc': 'تم إنشاء الميزة بنجاح',
    'content.feature.updated': 'تم تحديث الميزة',
    'content.feature.updatedDesc': 'تم تحديث الميزة بنجاح',
    'content.feature.deleted': 'تم حذف الميزة',
    'content.feature.deletedDesc': 'تم حذف الميزة بنجاح',
    'content.feature.add': 'إضافة ميزة',
    'content.feature.edit': 'تعديل الميزة',
    'content.feature.order': 'الترتيب',
    'content.feature.icon': 'الأيقونة',
    'content.feature.titleEn': 'العنوان (إنجليزي)',
    'content.feature.titleAr': 'العنوان (عربي)',
    'content.feature.descriptionEn': 'الوصف (إنجليزي)',
    'content.feature.descriptionAr': 'الوصف (عربي)',
    'content.feature.confirmDelete': 'هل أنت متأكد من حذف هذه الميزة؟',
    'content.feature.editDesc': 'قم بتعديل بيانات الميزة',
    'content.feature.addDesc': 'قم بإضافة ميزة جديدة',
    'content.feature.title': 'العنوان',
    'content.feature.description': 'الوصف',
    'content.feature.status': 'الحالة',
    
    'content.statistic.created': 'تم إنشاء الإحصائية',
    'content.statistic.createdDesc': 'تم إنشاء الإحصائية بنجاح',
    'content.statistic.updated': 'تم تحديث الإحصائية',
    'content.statistic.updatedDesc': 'تم تحديث الإحصائية بنجاح',
    'content.statistic.deleted': 'تم حذف الإحصائية',
    'content.statistic.deletedDesc': 'تم حذف الإحصائية بنجاح',
    'content.statistic.add': 'إضافة إحصائية',
    'content.statistic.edit': 'تعديل الإحصائية',
    'content.statistic.order': 'الترتيب',
    'content.statistic.number': 'الرقم',
    'content.statistic.labelEn': 'التسمية (إنجليزي)',
    'content.statistic.labelAr': 'التسمية (عربي)',
    'content.statistic.confirmDelete': 'هل أنت متأكد من حذف هذه الإحصائية؟',
    'content.statistic.editDesc': 'قم بتعديل بيانات الإحصائية',
    'content.statistic.addDesc': 'قم بإضافة إحصائية جديدة',
    'content.statistic.label': 'التسمية',
    'content.statistic.status': 'الحالة',
    
    'content.step.created': 'تم إنشاء الخطوة',
    'content.step.createdDesc': 'تم إنشاء الخطوة بنجاح',
    'content.step.updated': 'تم تحديث الخطوة',
    'content.step.updatedDesc': 'تم تحديث الخطوة بنجاح',
    'content.step.deleted': 'تم حذف الخطوة',
    'content.step.deletedDesc': 'تم حذف الخطوة بنجاح',
    'content.step.add': 'إضافة خطوة',
    'content.step.edit': 'تعديل الخطوة',
    'content.step.stepNumber': 'رقم الخطوة',
    'content.step.icon': 'الأيقونة',
    'content.step.titleEn': 'العنوان (إنجليزي)',
    'content.step.titleAr': 'العنوان (عربي)',
    'content.step.descriptionEn': 'الوصف (إنجليزي)',
    'content.step.descriptionAr': 'الوصف (عربي)',
    'content.step.confirmDelete': 'هل أنت متأكد من حذف هذه الخطوة؟',
    'content.step.editDesc': 'قم بتعديل بيانات الخطوة',
    'content.step.addDesc': 'قم بإضافة خطوة جديدة',
    'content.step.title': 'العنوان',
    'content.step.description': 'الوصف',
    'content.step.status': 'الحالة',
    
    'content.faq.created': 'تم إنشاء السؤال',
    'content.faq.createdDesc': 'تم إنشاء السؤال بنجاح',
    'content.faq.updated': 'تم تحديث السؤال',
    'content.faq.updatedDesc': 'تم تحديث السؤال بنجاح',
    'content.faq.deleted': 'تم حذف السؤال',
    'content.faq.deletedDesc': 'تم حذف السؤال بنجاح',
    'content.faq.add': 'إضافة سؤال',
    'content.faq.edit': 'تعديل السؤال',
    'content.faq.order': 'الترتيب',
    'content.faq.questionEn': 'السؤال (إنجليزي)',
    'content.faq.questionAr': 'السؤال (عربي)',
    'content.faq.answerEn': 'الإجابة (إنجليزي)',
    'content.faq.answerAr': 'الإجابة (عربي)',
    'content.faq.confirmDelete': 'هل أنت متأكد من حذف هذا السؤال؟',
    'content.faq.editDesc': 'قم بتعديل بيانات السؤال',
    'content.faq.addDesc': 'قم بإضافة سؤال جديد',
    'content.faq.question': 'السؤال',
    'content.faq.answer': 'الجواب',
    'content.faq.status': 'الحالة',
    
    'content.navigation.created': 'تم إنشاء الرابط',
    'content.navigation.createdDesc': 'تم إنشاء الرابط بنجاح',
    'content.navigation.updated': 'تم تحديث الرابط',
    'content.navigation.updatedDesc': 'تم تحديث الرابط بنجاح',
    'content.navigation.deleted': 'تم حذف الرابط',
    'content.navigation.deletedDesc': 'تم حذف الرابط بنجاح',
    'content.navigation.edit': 'تعديل الرابط',
    'content.navigation.order': 'الترتيب',
    'content.navigation.labelEn': 'التسمية (إنجليزي)',
    'content.navigation.labelAr': 'التسمية (عربي)',
    'content.navigation.url': 'الرابط',
    'content.navigation.routeName': 'اسم المسار',
    'content.navigation.confirmDelete': 'هل أنت متأكد من حذف هذا الرابط؟',
    'content.navigation.editDesc': 'قم بتعديل بيانات الرابط',
    'content.navigation.addDesc': 'قم بإضافة رابط جديد',
    'content.navigation.label': 'التسمية',
    'content.navigation.isExternal': 'خارجي',
    'content.navigation.status': 'الحالة',
    
    'content.comingSoon': 'قريباً: سيتم إضافة هذه الوظيفة قريباً',
    
    // Background
    'hero.subtitle': 'نظام احترافي لإدارة المناقصات والمشاريع في قطاع المقاولات. سجّل الآن واحصل على الفرص المناسبة لتخصصك.',
    'hero.cta': 'ابدأ الآن مجاناً',
    'hero.learnMore': 'كيف يعمل الموقع',
    
    // Features
    'features.title': 'لماذا KYNZ؟',
    'features.tenders': 'إدارة المناقصات',
    'features.tendersDesc': 'تصفح المناقصات المتاحة حسب تخصصك وقدّم عروضك بسهولة',
    'features.easy': 'معرض الأعمال',
    'features.easyDesc': 'اعرض مشاريعك السابقة وبناء سمعتك المهنية',
    'features.secure': 'تواصل مباشر',
    'features.secureDesc': 'تواصل مع الإدارة ومتابعة حالة طلباتك في الوقت الفعلي',
    'features.fast': 'أمان وموثوقية',
    'features.fastDesc': 'حماية كاملة لبياناتك ومستنداتك الرسمية',
    
    // Stats
    'stats.tenders': 'مناقصة منشورة',
    'stats.clients': 'عميل مسجل',
    'stats.projects': 'مشروع منفذ',
    'stats.specialties': 'تخصص',
    
    // Latest Tenders
    'latestTenders.viewAll': 'عرض جميع المشاريع',
    
    // CTA
    'cta.title': 'انضم إلينا اليوم',
    'cta.subtitle': 'سجل الآن واحصل على إشعارات بأحدث المناقصات في تخصصك',
    'cta.button': 'سجل مجاناً',
    
    // Dashboard CTA (for authenticated users)
    'cta.dashboard.title.admin': 'مرحباً بك في لوحة البيانات',
    'cta.dashboard.title.client': 'مرحباً بك في حسابك',
    'cta.dashboard.subtitle.admin': 'إدارة المنصة والتحكم في جميع الميزات',
    'cta.dashboard.subtitle.client': 'إدارة مشاريعك ومتابعة المناقصات',
    'cta.dashboard.button': 'لوحة البيانات',
    
    // Dashboard
    'dashboard.welcome': 'مرحباً',
    'dashboard.welcomeMessage': 'إليك ملخص نشاطك على المنصة',
    'dashboard.totalOffers': 'إجمالي العروض',
    'dashboard.pendingOffers': 'العروض المعلقة',
    'dashboard.acceptedOffers': 'العروض المقبولة',
    'dashboard.rejectedOffers': 'العروض المرفوضة',
    'dashboard.totalProjects': 'إجمالي المشاريع',
    'dashboard.approvedProjects': 'المشاريع المعتمدة',
    'dashboard.activeTenders': 'مناقصات متاحة',
    'dashboard.myApplications': 'طلبات مقدمة',
    'dashboard.pending': 'بانتظار المراجعة',
    'dashboard.accepted': 'طلبات مقبولة',
    'dashboard.latestTenders': 'أحدث المناقصات',
    'dashboard.recentTenders': 'المناقصات الأخيرة',
    'dashboard.noRecentTenders': 'لا توجد مناقصات حديثة',
    'dashboard.notifications': 'الإشعارات',
    'dashboard.viewAll': 'عرض الكل',
    'dashboard.browseTenders': 'تصفح المناقصات',
    'dashboard.findNewTenders': 'ابحث عن فرص جديدة',
    'dashboard.myPortfolio': 'معرض أعمالي',
    'dashboard.managePortfolio': 'إدارة معرض الأعمال',
    'dashboard.myTenders': 'مناقصاتي',
    
    // Tender Details
    'tenders.tenderDetails': 'تفاصيل المناقصة',
    'tenders.basicInfo': 'المعلومات الأساسية',
    'tenders.budget': 'الميزانية',
    'tenders.minBudget': 'الحد الأدنى',
    'tenders.maxBudget': 'الحد الأقصى',
    'tenders.deadlineNote': 'آخر موعد لتقديم العروض',
    'tenders.tenderNotFound': 'لم يتم العثور على المناقصة',
    'common.back': 'العودة',
    'common.documents': 'المستندات',
    'common.dragAndDrop': 'اسحب وأفلت الملفات هنا أو انقر لتحديدها',
    'common.allowedFileTypes': 'PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG (الحد الأقصى: 10 ميجابايت للملف الواحد)',
    'common.allSpecializations': 'جميع التخصصات',
    
    // Status
    'status.pending': 'قيد المراجعة',
    'status.approved': 'معتمد',
    'status.rejected': 'مرفوض',
    'status.suspended': 'موقوف',
    
    // Settings
    'settings.title': 'الإعدادات',
    'settings.language': 'اللغة',
    'settings.notifications': 'الإشعارات',
    'settings.adminSettings': 'إعدادات الأدمن',
    'settings.fileSizeSettings': 'إعدادات حجم الملفات',
    'settings.maxFileSize': 'الحد الأقصى لحجم الملف',
    'settings.maxFileSizeDesc': 'الحجم الأقصى للملفات التي يمكن للمستخدمين رفعها (بالميجابايت)',
    'settings.enterCustomSize': 'أدخل الحجم المخصص بالميجابايت',
    'settings.addCustomFileType': 'إضافة نوع ملف مخصص',
    'settings.enterFileExtension': 'أدخل امتداد الملف (بدون نقطة)',
    'settings.selectedFileTypes': 'أنواع الملفات المحددة',
    'settings.emailHost': 'خادم البريد (Host)',
    'settings.emailPort': 'منفذ البريد (Port)',
    'settings.emailUsername': 'اسم المستخدم',
    'settings.emailPassword': 'كلمة المرور',
    'settings.emailFrom': 'البريد المرسل منه',
    'settings.emailFromName': 'اسم المرسل',
    'settings.emailEncryption': 'التشفير',
    'settings.save': 'حفظ',
    'settings.saved': 'تم الحفظ',
    'settings.savedDesc': 'تم حفظ الإعدادات بنجاح',
    'settings.mb': 'ميجابايت',
    'settings.selectFileTypes': 'اختر أنواع الملفات',
    
    // Auth
    'auth.confirmPassword': 'تأكيد كلمة المرور',
    'auth.accountCreated': 'تم إنشاء الحساب',
    'auth.loggedIn': 'تم تسجيل الدخول',
    'auth.accountReview': 'سيتم مراجعة حسابك والتواصل معك قريباً',
    'auth.welcomeBack': 'مرحباً بك في منصة KYNZ',
    'auth.createAccount': 'إنشاء حساب جديد',
    'auth.login': 'تسجيل الدخول',
    'auth.joinPlatform': 'انضم إلى منصة المناقصات الأولى',
    'auth.welcomeBackMessage': 'مرحباً بعودتك إلى KYNZ',
    'auth.fullName': 'الاسم الكامل',
    'auth.phone': 'رقم الهاتف',
    'auth.email': 'البريد الإلكتروني',
    'auth.password': 'كلمة المرور',
    'auth.forgotPassword': 'نسيت كلمة المرور؟',
    'auth.haveAccount': 'لديك حساب بالفعل؟',
    'auth.noAccount': 'ليس لديك حساب؟',
    'auth.registerNow': 'سجّل الآن',
    'auth.backToHome': 'العودة للرئيسية',
    'auth.passwordChanged': 'تم تغيير كلمة المرور',
    'auth.passwordChangedDesc': 'تم تحديث كلمة المرور بنجاح',
    'auth.changePassword': 'تغيير كلمة المرور',
    'auth.currentPassword': 'كلمة المرور الحالية',
    'auth.newPassword': 'كلمة المرور الجديدة',
    'auth.updatePassword': 'تحديث كلمة المرور',
    'auth.loginFailed': 'فشل تسجيل الدخول',
    'auth.registerFailed': 'فشل إنشاء الحساب',
    'auth.emailPlaceholder': 'أدخل بريدك الإلكتروني',
    'auth.passwordPlaceholder': 'أدخل كلمة المرور',
    'auth.secureConnection': 'اتصال آمن',
    'auth.dataProtection': 'حماية البيانات',
    'auth.support247': 'دعم على مدار الساعة',
    'auth.basicInfo': 'المعلومات الأساسية',
    'auth.basicInfoDesc': 'أدخل معلوماتك الأساسية لإنشاء حساب',
    'auth.companyInfo': 'معلومات الشركة',
    'auth.companyInfoDesc': 'أدخل معلومات شركتك وتخصصاتك',
    'auth.documents': 'المستندات المطلوبة',
    'auth.requiredDocuments': 'المستندات المطلوبة',
    'auth.documentsDesc': 'ارفع المستندات الرسمية المطلوبة',
    'auth.step': 'الخطوة',
    'auth.of': 'من',
    'auth.previous': 'السابق',
    'auth.next': 'التالي',
    'auth.submit': 'إرسال',
    'auth.registering': 'جاري التسجيل...',
    'auth.platformFeatures': 'مميزات المنصة',
    'auth.securePlatform': 'منصة آمنة',
    'auth.fastEasy': 'سريع وسهل',
    'auth.expertSupport': 'دعم خبراء',
    'auth.registrationSuccess': 'تم التسجيل بنجاح',
    'auth.registrationSuccessDesc': 'سيتم مراجعة حسابك والتواصل معك قريباً',
    'auth.backToLogin': 'العودة لتسجيل الدخول',
    'auth.cancel': 'إلغاء',
    'auth.completed': 'مكتمل',
    'auth.dragDropFile': 'اسحب وأفلت الملف هنا',
    'auth.supportedFormats': 'الصيغ المدعومة',
    'auth.maxFileSize': 'الحد الأقصى لحجم الملف',
    'auth.securityNotice': 'ملاحظة أمنية',
    'auth.securityNoticeDesc': 'بياناتك آمنة ومحمية',
    'auth.completeRegistration': 'إكمال التسجيل',
    'auth.secure100': 'آمن 100%',
    'auth.easyToUse': 'سهل الاستخدام',
    'auth.realOpportunities': 'فرص حقيقية',
    
    // Validation
    'validation.required': 'هذا الحقل مطلوب',
    'validation.email': 'البريد الإلكتروني غير صحيح',
    'validation.passwordMin': 'كلمة المرور يجب أن تكون 8 أحرف على الأقل',
    'validation.passwordMatch': 'كلمات المرور غير متطابقة',
    'validation.specializationRequired': 'يجب اختيار تخصص واحد على الأقل',
    'validation.documentRequired': 'هذا المستند مطلوب',
    'validation.fileSize': 'حجم الملف كبير جداً',
    'validation.fileType': 'نوع الملف غير مدعوم',
    
    // Profile
    'profile.licenseNumber': 'رقم الرخصة',
    'profile.taxId': 'الرقم الضريبي',
    'profile.profileImage': 'صورة الملف الشخصي',
    'profile.profileImageDesc': 'صورة شخصية أو شعار الشركة (اختياري)',
    'profile.selectSpecializations': 'اختر التخصصات',
    'profile.specializations': 'التخصصات',
    'profile.specializationsDesc': 'اختر التخصصات التي تناسب عملك',
    'profile.uploadDocuments': 'رفع المستندات',
    'profile.uploadDocumentsDesc': 'ارفع المستندات الرسمية المطلوبة',
    'profile.commercialRegister': 'السجل التجاري',
    'profile.taxCertificate': 'شهادة الضريبة',
    'profile.ibanCertificate': 'شهادة IBAN',
    'profile.zakatIncomeCertificate': 'شهادة الزكاة والدخل',
    'profile.socialInsuranceCertificate': 'شهادة المحتوى',
    'profile.companyProfile': 'ملف الشركة',
    
    // Portfolio
    'portfolio.clientPortfolio': 'معرض أعمالي',
    'portfolio.addProject': 'أضف مشاريعك لتعرضها على عملائك',
    'portfolio.totalProjects': 'إجمالي المشاريع',
    'portfolio.status': 'الحالة',
    'portfolio.addNew': 'إضافة سابقة أعمال',
    'portfolio.confirmDelete': 'هل أنت متأكد من حذف هذا المشروع؟',
    'portfolio.adminNotAvailable': 'معرض الأعمال متاح للعملاء فقط',
    'portfolio.adminMessage': 'المسؤولون ليس لديهم قسم معرض أعمال. يرجى الانتقال إلى لوحة تحكم المسؤول.',
    'portfolio.goToAdmin': 'الذهاب إلى لوحة تحكم المسؤول',
    'portfolio.noProjects': 'لا توجد مشاريع',
    'portfolio.noProjectsDesc': 'ابدأ بإضافة أول مشروع لعرضه على عملائك',
    'portfolio.addFirstProject': 'إضافة أول سابقة أعمال',
    'portfolio.editProjectTitle': 'تعديل مشروع',
    'portfolio.addNewTitle': 'إضافة سابقة أعمال',
    'portfolio.editProjectDesc': 'تعديل تفاصيل المشروع',
    'portfolio.addProjectDesc': 'إضافة سابقة أعمال جديدة إلى معرض أعمالك',
    'portfolio.projectDetails': 'تفاصيل المشروع',
    'portfolio.newProjectDetails': 'تفاصيل سابقة الأعمال الجديدة',
    'portfolio.projectInfo': 'معلومات المشروع',
    'portfolio.projectNameAr': 'اسم المشروع (عربي)',
    'portfolio.projectNameEn': 'Project Name (English)',
    'portfolio.projectDescriptionAr': 'وصف المشروع (عربي)',
    'portfolio.projectDescriptionEn': 'Project Description (English)',
    'portfolio.projectLocationAr': 'الموقع (عربي)',
    'portfolio.projectLocationEn': 'Location (English)',
    'portfolio.year': 'السنة',
    'portfolio.month': 'الشهر',
    'portfolio.day': 'اليوم',
    'portfolio.completedDate': 'تاريخ الإنجاز',
    'portfolio.projectImages': 'صور المشروع',
    'portfolio.dragDropImages': 'اسحب وأفلت الصور هنا أو انقر للاختيار',
    'portfolio.supportedImageFormats': 'الصيغ المدعومة: JPG, PNG, WEBP',
    'portfolio.maxImageSize': 'الحد الأقصى للصورة: 5 ميجابايت',
    'portfolio.projectAdded': 'تم إضافة المشروع بنجاح',
    'portfolio.projectUpdated': 'تم تحديث المشروع بنجاح',
    'portfolio.addError': 'فشل إضافة المشروع',
    'portfolio.updateError': 'فشل تحديث المشروع',
    'portfolio.viewProject': 'عرض المشروع',
    'portfolio.editProject': 'تعديل المشروع',
    'portfolio.deleteProject': 'حذف المشروع',
    'portfolio.imageError': 'يرجى اختيار ملفات صور فقط',
    'portfolio.requiredField': 'هذا الحقل مطلوب',
    'portfolio.projectName': 'اسم المشروع',
    'portfolio.description': 'وصف المشروع',
    'portfolio.locationAr': 'الموقع',
    'portfolio.locationEn': 'Location',
    'portfolio.selectDate': 'اختر التاريخ',
    'portfolio.uploadImages': 'رفع الصور',
    'portfolio.saveProject': 'حفظ المشروع',
    'portfolio.cancel': 'إلغاء',
    'portfolio.loading': 'جاري التحميل...',
    'portfolio.noImages': 'لا توجد صور',
    'portfolio.addImages': 'إضافة صور',
    'portfolio.removeImage': 'إزالة الصورة',
    'portfolio.projectTitle': 'عنوان المشروع',
    'portfolio.projectContent': 'محتوى المشروع',
    'portfolio.viewingProject': 'عرض المشروع',
    'portfolio.actions': 'الإجراءات',
    'portfolio.backToPortfolio': 'العودة إلى معرض الأعمال',
    'portfolio.projectId': 'رقم المشروع',
    'portfolio.totalImages': 'إجمالي الصور',
    'portfolio.images': 'الصور',
    'portfolio.location': 'الموقع',
    'portfolio.imageFormats': 'صيغ الصور',
    'portfolio.existingImages': 'الصور الموجودة',
    'portfolio.totalImagesLabel': 'إجمالي الصور:',
    'portfolio.joinUs': 'انضم إلينا',
    'portfolio.contactUs': 'تواصل معنا',
    'portfolio.ourProjects': 'مشاريعنا',
    'portfolio.projectsDescription': 'استعرض أحدث مشاريعنا المنجزة',
    'portfolio.requestProject': 'طلب مشروع',
    'portfolio.readyToStart': 'جاهز للبدء؟',
    'portfolio.readyDescription': 'تواصل معنا اليوم لمناقشة مشروعك القادم',
    'portfolio.getStarted': 'ابدأ الآن',
    'portfolio.learnMore': 'اعرف المزيد',
    'portfolio.distinguishedProjects': 'مشاريع متميزة',
    'portfolio.highQuality': 'جودة عالية',
    'portfolio.comprehensiveGuarantees': 'ضمانات متكاملة',
    
    // Documents
    'documents.commercialRegister': 'السجل التجاري',
    'documents.taxCertificate': 'شهادة الضريبة',
    'documents.ibanCertificate': 'شهادة IBAN',
    'documents.zakatCertificate': 'شهادة الزكاة والدخل',
    'documents.socialInsurance': 'شهادة المحتوى',
    'documents.companyProfile': 'ملف الشركة التعريفي',
    'documents.commercialRegisterDesc': 'السجل التجاري الرسمي للشركة',
    'documents.taxCertificateDesc': 'شهادة الضريبة الصادرة من الجهات المختصة',
    'documents.ibanCertificateDesc': 'شهادة الحساب البنكي الدولي (IBAN)',
    'documents.zakatCertificateDesc': 'شهادة الزكاة والدخل السنوية',
    'documents.socialInsuranceDesc': 'شهادة المحتوى للمحتوى الرقمي (اختياري)',
    'documents.companyProfileDesc': 'ملف تعريفي شامل عن الشركة وأنشطتها',
    
    // Additional Profile Keys
    'profile.overview': 'نظرة عامة',
    'profile.editProfile': 'تعديل الملف',
    'profile.documents': 'الوثائق',
    'profile.changePassword': 'تغيير كلمة المرور',
    'profile.companyInfo': 'معلومات الشركة',
    'profile.companyName': 'اسم الشركة',
    'profile.email': 'البريد الإلكتروني',
    'profile.phone': 'رقم الهاتف',
    'profile.address': 'العنوان',
    'profile.description': 'وصف',
    'profile.profilePicture': 'صورة الملف الشخصي',
    'profile.cannotDeleteApproved': 'لا يمكن حذف مشروع معتمد',
    'profile.approvedByAdmin': 'تمت الموافقة من قبل المسؤول',
    'profile.approved': 'معتمد',
    'profile.emailReadOnly': 'البريد الإلكتروني للقراءة فقط',
    'profile.noSpecializations': 'لا توجد تخصصات',
    'profile.personalInfo': 'معلوماتي الشخصية',
    
    // Password Change
    'auth.passwordMismatch': 'خطأ في كلمة المرور',
    'auth.passwordsDoNotMatch': 'كلمة المرور الجديدة وتأكيد كلمة المرور غير متطابقين',
    'auth.passwordTooShort': 'كلمة المرور قصيرة',
    'auth.passwordMinLength': 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
    
    // Common
    'common.approved': 'معتمد',
    'common.rejected': 'مرفوض',
    'common.pending': 'قيد المراجعة',
    'common.error': 'حدث خطأ',
    'common.success': 'نجاح',
    'common.retry': 'إعادة المحاولة',
    'common.download': 'تحميل',
    'common.uploading': 'جاري الرفع...',
    'common.fileSelected': 'ملف محدد',
    'common.filesSelected': 'ملفات محددة',
    'readOnly.disabled': 'هذا الإجراء معطل',
    
    // Dashboard
    'dashboard.summary': 'إليك ملخص نشاطك اليوم',
    'dashboard.deadline': 'آخر موعد',
    'dashboard.tenderNew': 'جديد',
    'dashboard.tenderUrgent': 'عاجل',
    'dashboard.notificationAccepted': 'تم قبول طلبك في مناقصة توريد الحديد',
    'dashboard.notificationNewTender': 'مناقصة جديدة تناسب تخصصك',
    'dashboard.notificationReminder': 'تذكير: موعد إغلاق المناقصة غداً',
    'dashboard.timeAgo1h': 'منذ ساعة',
    'dashboard.timeAgo3h': 'منذ 3 ساعات',
    'dashboard.timeAgo5h': 'منذ 5 ساعات',
    'dashboard.tenderSteel': 'مناقصة توريد حديد تسليح',
    'dashboard.tenderPaint': 'مناقصة دهانات مبنى إداري',
    'dashboard.tenderCement': 'توريد اسمنت مقاوم',
    
    // Profile
    'profile.title': 'الملف الشخصي',
    'profile.subtitle': 'إدارة بيانات شركتك ومستنداتك',
    'profile.basicInfo': 'المعلومات الأساسية',
    'profile.specialization': 'التخصص',
    'profile.selectSpecialization': 'اختر التخصص',
    'profile.companyDescription': 'نبذة عن الشركة',
    'profile.saveChanges': 'حفظ التغييرات',
    'profile.changesSaved': 'تم حفظ التغييرات',
    'profile.changesSavedDesc': 'تم تحديث بيانات الملف الشخصي بنجاح',
    'profile.zakatCertificate': 'شهادة الزكاة والدخل',
    'profile.isoCertificate': 'شهادة الجودة ISO',
    'profile.verified': 'معتمد',
    'profile.pending': 'قيد المراجعة',
    'profile.uploadDocument': 'رفع مستند جديد',
    'profile.specialization.steel': 'حديد',
    'profile.specialization.cement': 'اسمنت',
    'profile.specialization.paint': 'دهان',
    'profile.specialization.construction': 'إنشاءات عامة',
    'profile.specialization.infrastructure': 'بنية تحتية',
    'profile.currentPassword': 'كلمة المرور الحالية',
    'profile.enterCurrentPassword': 'أدخل كلمة المرور الحالية',
    'profile.newPassword': 'كلمة المرور الجديدة',
    'profile.enterNewPassword': 'أدخل كلمة المرور الجديدة',
    'profile.confirmNewPassword': 'تأكيد كلمة المرور الجديدة',
    'profile.enterConfirmNewPassword': 'أكد كلمة المرور الجديدة',
    'profile.noDocuments': 'لا توجد مستندات',
    'profile.optional': 'اختياري',
    'profile.allowedFiles': 'PDF, DOC, DOCX, JPG, PNG',
    'profile.searchSpecializations': 'ابحث عن التخصصات...',
    'profile.noSpecializationsFound': 'لم يتم العثور على تخصصات',
    'tenders.browse': 'تصفح أحدث المناقصات وسجّل للحصول على التفاصيل الكاملة',
    'tenders.search': 'بحث في المناقصات...',
    'tenders.specialization': 'التخصص',
    'tenders.allSpecializations': 'جميع التخصصات',
    'tenders.region': 'المنطقة',
    'tenders.allRegions': 'جميع المناطق',
    'tenders.open': 'متاحة',
    'tenders.closed': 'مغلقة',
    'tenders.closingSoon': 'قريبًا تنتهي',
    'tenders.new': 'جديد',
    'tenders.deadline': 'الموعد النهائي',
    'tenders.finalDeadline': 'الموعد النهائي النهائي',
    'tenders.submitOffer': 'التقديم على المناقصة',
    'tenders.documents': 'المستندات',
    'tenders.viewDetails': 'عرض التفاصيل',
    'tenders.registerMessage': 'سجل للحصول على التفاصيل الكاملة وإرسال العروض',
    'tenders.registerNow': 'سجل الآن',
    'tenders.riyadh': 'رياض',
    'tenders.jeddah': 'جدة',
    'tenders.dammam': 'الدمام',
    'tenders.makkah': 'مكة المكرمة',
    'tenders.steel': 'قطع معدنية',
    'tenders.cement': 'رمال',
    'tenders.paint': 'لوحات شبكة',
    'tenders.sar': 'SAR',
    
    // Applications
    'applications.title': 'طلباتي',
    'applications.subtitle': 'متابعة حالة الطلبات المقدمة',
    'applications.all': 'الكل',
    'applications.pending': 'قيد المراجعة',
    'applications.accepted': 'مقبولة',
    'applications.rejected': 'مرفوضة',
    'applications.noApplications': 'لا توجد طلبات',
    'applications.submittedDate': 'تاريخ التقديم',
    'applications.submittedPrice': 'السعر المقدم',
    'applications.rejectedBadge': 'مرفوض',
    'applications.acceptedBadge': 'مقبول',
    'applications.attachments': 'المرفقات',
    
    // Offers
    'offers.title': 'العروض',
    'offers.subtitle': 'إدارة العروض المقدمة',
    'offers.myOffers': 'عروضي',
    'offers.allOffers': 'جميع العروض',
    'offers.submitOffer': 'التقديم على المناقصة',
    'offers.submitNew': 'التقديم على المناقصة',
    'offers.fileTooLarge': 'الملف كبير جداً',
    'offers.maxSize': 'الحد الأقصى 10MB',
    'offers.invalidFileType': 'نوع الملف غير مدعوم',
    'offers.amountOutOfRange': 'المبلغ خارج النطاق المسموح',
    'offers.documentsRequired': 'يجب رفع مستندات مالية أو فنية على الأقل',
    'offers.edit': 'تعديل',
    'offers.delete': 'حذف',
    'offers.accept': 'قبول',
    'offers.reject': 'رفض',
    'offers.viewDetails': 'عرض التفاصيل',
    'offers.tender': 'المناقصة',
    'offers.client': 'العميل',
    'offers.proposedAmount': 'المبلغ المقترح',
    'offers.notes': 'ملاحظات',
    'offers.status': 'الحالة',
    'offers.pending': 'قيد المراجعة',
    'offers.statusAccepted': 'مقبول',
    'offers.statusRejected': 'مرفوض',
    'offers.submittedDate': 'تاريخ التقديم',
    'offers.noOffers': 'لا توجد عروض',
    'offers.confirmDelete': 'هل أنت متأكد من حذف هذا العرض؟',
    'offers.confirmAccept': 'هل أنت متأكد من قبول هذا العرض؟',
    'offers.confirmReject': 'هل أنت متأكد من رفض هذا العرض؟',
    'offers.submitted': 'تم تقديم العرض',
    'offers.submittedDesc': 'تم تقديم العرض بنجاح',
    'offers.updated': 'تم تحديث العرض',
    'offers.updatedDesc': 'تم تحديث العرض بنجاح',
    'offers.deleted': 'تم حذف العرض',
    'offers.deletedDesc': 'تم حذف العرض بنجاح',
    'offers.acceptedTitle': 'تم قبول العرض',
    'offers.acceptedDesc': 'تم قبول العرض بنجاح',
    'offers.rejectedTitle': 'تم رفض العرض',
    'offers.rejectedDesc': 'تم رفض العرض بنجاح',
    'offers.submitFailed': 'فشل تقديم العرض',
    'offers.submitFailedDesc': 'حدث خطأ أثناء تقديم العرض',
    'offers.updateFailed': 'فشل تحديث العرض',
    'offers.updateFailedDesc': 'حدث خطأ أثناء تحديث العرض',
    'offers.deleteFailed': 'فشل حذف العرض',
    'offers.deleteFailedDesc': 'حدث خطأ أثناء حذف العرض',
    'offers.acceptFailed': 'فشل قبول العرض',
    'offers.rejectFailed': 'فشل رفض العرض',
    'offers.tenderId': 'المناقصة',
    'offers.selectTender': 'اختر المناقصة',
    'offers.amount': 'المبلغ',
    'offers.amountPlaceholder': 'أدخل المبلغ المقترح',
    'offers.notesPlaceholder': 'أدخل ملاحظاتك (اختياري)',
    'offers.filterStatus': 'فلترة حسب الحالة',
    'offers.allStatuses': 'جميع الحالات',
    'offers.filterTender': 'فلترة حسب المناقصة',
    'offers.allTenders': 'جميع المناقصات',
    'offers.enterAmount': 'أدخل المبلغ',
    'offers.budgetRange': 'نطاق الميزانية',
    'offers.enterNotes': 'أدخل الملاحظات',
    'offers.attachments': 'المرفقات',
    'offers.selectFiles': 'اختر الملفات',
    'offers.allowedFiles': 'الملفات المسموحة: PDF, DOC, DOCX, JPG, PNG (حد أقصى 10MB)',
    'offers.offerDetails': 'تفاصيل العرض',
    'offers.financialDocuments': 'العرض المالي',
    'offers.financialDocumentsDesc': 'ارفع ملفات العرض المالي (الأسعار، التكاليف، إلخ)',
    'offers.technicalDocuments': 'العرض الفني',
    'offers.technicalDocumentsDesc': 'ارفع ملفات العرض الفني (المنهجية، الخبرات، إلخ)',
    'offers.documentType': 'نوع الملف',
    'offers.documentTypeFinancial': 'مالي',
    'offers.documentTypeTechnical': 'فني',
    
    
    // Tenders
    'tenders.noTenders': 'لا توجد مناقصات متاحة',
    'tenders.manage': 'إدارة المناقصات',
    'tenders.manageDesc': 'إنشاء وإدارة المناقصات',
    'tenders.create': 'إنشاء مناقصة',
    'tenders.edit': 'تعديل',
    'tenders.delete': 'حذف',
    'tenders.confirmDelete': 'هل أنت متأكد من حذف هذه المناقصة؟',
    'tenders.createTender': 'إنشاء مناقصة جديدة',
    'tenders.editTender': 'تعديل المناقصة',
    'tenders.createTenderDesc': 'قم بإنشاء مناقصة جديدة',
    'tenders.editTenderDesc': 'قم بتعديل بيانات المناقصة',
    'tenders.update': 'تحديث',
    'tenders.titleEn': 'العنوان (إنجليزي)',
    'tenders.titleAr': 'العنوان (عربي)',
    'tenders.titleEnPlaceholder': 'Enter title in English',
    'tenders.titleArPlaceholder': 'أدخل العنوان بالعربية',
    'tenders.titleRequired': 'يجب إدخال العنوان باللغتين',
    'tenders.descriptionEn': 'الوصف (إنجليزي)',
    'tenders.descriptionAr': 'الوصف (عربي)',
    'tenders.descriptionEnPlaceholder': 'Enter description in English',
    'tenders.descriptionArPlaceholder': 'أدخل الوصف بالعربية',
    'tenders.requirementsEn': 'المتطلبات (إنجليزي)',
    'tenders.requirementsAr': 'المتطلبات (عربي)',
    'tenders.requirementsEnPlaceholder': 'Enter requirements in English',
    'tenders.requirementsArPlaceholder': 'أدخل المتطلبات بالعربية',
    'tenders.requirements': 'المتطلبات',
    'tenders.budgetMin': 'الحد الأدنى للميزانية',
    'tenders.budgetMax': 'الحد الأقصى للميزانية',
    'tenders.budgetError': 'الحد الأدنى يجب أن يكون أقل من الحد الأقصى',
    'tenders.selectDeadline': 'اختر التاريخ',
    'tenders.status': 'الحالة',
    'tenders.pending': 'قيد المراجعة',
    'tenders.visibilityType': 'نوع الرؤية',
    'tenders.allClients': 'جميع العملاء',
    'tenders.bySpecializations': 'حسب الاختصاصات',
    'tenders.specificClients': 'عملاء محددون',
    'tenders.selectSpecializations': 'اختر الاختصاصات',
    'tenders.selectClients': 'اختر العملاء',
    'tenders.specializationsRequired': 'يجب اختيار اختصاص واحد على الأقل',
    'tenders.clientsRequired': 'يجب اختيار عميل واحد على الأقل',
    'tenders.allStatuses': 'جميع الحالات',
    'tenders.filterStatus': 'فلترة حسب الحالة',
    'tenders.created': 'تم إنشاء المناقصة',
    'tenders.updated': 'تم تحديث المناقصة',
    'tenders.deleted': 'تم حذف المناقصة',
    'tenders.createdDesc': 'تم إنشاء المناقصة بنجاح',
    'tenders.updatedDesc': 'تم تحديث المناقصة بنجاح',
    'tenders.deletedDesc': 'تم حذف المناقصة بنجاح',
    'tenders.createFailed': 'فشل إنشاء المناقصة',
    'tenders.updateFailed': 'فشل تحديث المناقصة',
    'tenders.deleteFailed': 'فشل حذف المناقصة',
    
    // Admin Tenders 
    'admin.tenders.title': 'إدارة المناقصات',
    'admin.tenders.subtitle': 'عرض وإدارة جميع المناقصات في النظام',
    'admin.tenders.createTender': 'إنشاء مناقصة',
    'admin.tenders.searchPlaceholder': 'البحث عن مناقصة...',
    'admin.tenders.allStatuses': 'جميع الحالات',
    'admin.tenders.filterStatus': 'فلترة حسب الحالة',
    'admin.tenders.columnTitle': 'العنوان',
    'admin.tenders.budget': 'الميزانية',
    'admin.tenders.deadline': 'الموعد النهائي',
    'admin.tenders.status': 'الحالة',
    'admin.tenders.created': 'تاريخ الإنشاء',
    'admin.tenders.actions': 'الإجراءات',
    'admin.tenders.minBudget': 'الحد الأدنى للميزانية',
    'admin.tenders.maxBudget': 'الحد الأقصى للميزانية',
    'admin.tenders.view': 'عرض',
    'admin.tenders.edit': 'تعديل',
    'admin.tenders.delete': 'حذف',
    'admin.tenders.activate': 'تفعيل',
    'admin.tenders.close': 'إغلاق',
    'admin.tenders.adminOnly': 'هذه الصفحة متاحة فقط للمسؤولين',
    'admin.tenders.noTenders': 'لا توجد مناقصات',
    'admin.tenders.tenderDetails': 'تفاصيل المناقصة',
    'admin.tenders.tenderDetailsDesc': 'معلومات تفصيلية عن المناقصة',
    'admin.tenders.basicInfo': 'المعلومات الأساسية',
    'admin.tenders.documents': 'المستندات',
    'admin.tenders.uploadInstructions': 'اسحب وأفلت الملفات هنا أو انقر لتحديدها',
    'admin.tenders.noDocuments': 'لا توجد مستندات مرفوعة',
    'admin.tenders.uploadDocuments': 'رفع المستندات',
    'admin.tenders.allowedFileTypes': 'PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG (حد أقصى: 10MB لكل ملف)',
    'admin.tenders.uploadedFiles': 'الملفات المرفوعة',
    'admin.tenders.statusAndDates': 'الحالة والتواريخ',
    'admin.tenders.createdAt': 'تاريخ الإنشاء',
    'admin.tenders.visibility': 'نوع الرؤية',
    'admin.tenders.allUsers': 'جميع المستخدمين',
    'admin.tenders.specializationsOnly': 'التخصصات فقط',
    'admin.tenders.specificClients': 'عملاء محددون',
    'admin.tenders.specializations': 'التخصصات',
    'admin.tenders.editTender': 'تعديل المناقصة',
    'admin.tenders.editTenderDesc': 'تعديل معلومات المناقصة',
    'admin.tenders.createTenderDesc': 'إضافة مناقصة جديدة',
    'admin.tenders.titleEn': 'العنوان (إنجليزي)',
    'admin.tenders.titleAr': 'العنوان (عربي)',
    'admin.tenders.descriptionEn': 'الوصف (إنجليزي)',
    'admin.tenders.descriptionAr': 'الوصف (عربي)',
    'admin.tenders.requirementsEn': 'المتطلبات (إنجليزي)',
    'admin.tenders.requirementsAr': 'المتطلبات (عربي)',
    'admin.tenders.titleEnPlaceholder': 'أدخل عنوان المناقصة بالإنجليزية',
    'admin.tenders.titleArPlaceholder': 'أدخل عنوان المناقصة بالعربية',
    'admin.tenders.descriptionEnPlaceholder': 'أدخل وصف المناقصة بالإنجليزية',

    // Admin Users Management
    'admin.users.title': 'إدارة المسؤولين',
    'admin.users.subtitle': 'عرض وإدارة المسؤولين في النظام',
    'admin.users.create': 'إضافة مسؤول',
    'admin.users.edit': 'تعديل المسؤول',
    'admin.users.delete': 'حذف المسؤول',
    'admin.users.name': 'الاسم',
    'admin.users.email': 'البريد الإلكتروني',
    'admin.users.position': 'المنصب',
    'admin.users.isSuperAdmin': 'مدير النظام',
    'admin.users.status': 'الحالة',
    'admin.users.createdAt': 'تاريخ الإنشاء',
    'admin.users.actions': 'الإجراءات',
    'admin.users.noUsers': 'لا يوجد مسؤولين',
    'admin.users.searchPlaceholder': 'البحث عن مسؤول...',
    'admin.users.superAdminOnly': 'هذه الميزة متاحة فقط لمدير النظام',
    'admin.users.resetPassword': 'إعادة تعيين كلمة المرور',
    'admin.users.password': 'كلمة المرور',
    'admin.users.confirmPassword': 'تأكيد كلمة المرور',
    'admin.users.passwordPlaceholder': 'أدخل كلمة المرور',
    'admin.users.positionPlaceholder': 'أدخل المنصب (اختياري)',
    'admin.users.createSuccess': 'تم إنشاء المسؤول بنجاح',
    'admin.users.updateSuccess': 'تم تحديث المسؤول بنجاح',
    'admin.users.deleteSuccess': 'تم حذف المسؤول بنجاح',
    'admin.users.passwordResetSuccess': 'تم إعادة تعيين كلمة المرور بنجاح',
    'admin.users.cannotDeleteSelf': 'لا يمكنك حذف حسابك الخاص',
    'admin.users.cannotDeleteLastSuperAdmin': 'لا يمكن حذف آخر مدير نظام',
    'admin.users.cannotDemoteLastSuperAdmin': 'لا يمكن إلغاء صلاحيات آخر مدير نظام',
    'admin.tenders.descriptionArPlaceholder': 'أدخل وصف المناقصة بالعربية',
    'admin.tenders.requirementsEnPlaceholder': 'أدخل متطلبات المناقصة بالإنجليزية',
    'admin.tenders.requirementsArPlaceholder': 'أدخل متطلبات المناقصة بالعربية',
    'admin.tenders.minBudgetLabel': 'الحد الأدنى للميزانية',
    'admin.tenders.maxBudgetLabel': 'الحد الأقصى للميزانية',
    'admin.tenders.deadlinePlaceholder': 'mm/dd/yyyy',
    'admin.tenders.statusLabel': 'الحالة',
    'admin.tenders.visibilityLabel': 'نوع الرؤية',
    'admin.tenders.dragDrop': 'اسحب وأفلت الملفات هنا أو انقر للاختيارها',
    'admin.tenders.cancel': 'إلغاء',
    'admin.tenders.update': 'تحديث',
    'admin.tenders.description': 'الوصف',
    'admin.tenders.requirements': 'المتطلبات',
    'admin.tenders.offers': 'العروض',
    'admin.tenders.details': 'تفاصيل',
    'admin.tenders.financialInfo': 'المعلومات المالية',
    
    // Dashboard
    'dashboard.on': 'في',
    'dashboard.proposedAmount': 'المبلغ المقترح',
    'dashboard.submittedBy': 'مقدم من قبل',
    'dashboard.viewOffer': 'عرض العرض',
    'dashboard.manageClients': 'إدارة العملاء',
    'dashboard.viewManageClients': 'عرض إدارة العملاء',
    'dashboard.manageTenders': 'إدارة المناقصات',
    'dashboard.viewManageTenders': 'عرض إدارة المناقصات',
    'dashboard.manageContent': 'إدارة المحتوى',
    'dashboard.viewManageContent': 'عرض إدارة المحتوى',
    'dashboard.trackApplications': 'متابعة الطلبات',
    
    // Specializations
    'specializations.confirmDelete': 'هل أنت متأكد من حذف هذا التخصص؟',
    
    
    // Dashboard
    'dashboard.noTenders': 'لا توجد مناقصات',
    'dashboard.noNotifications': 'لا توجد إشعارات',
    'dashboard.totalTenders': 'إجمالي المناقصات',
    'dashboard.totalClients': 'إجمالي العملاء',
    'dashboard.pendingClients': 'العملاء المعلقة',
    
    
    // Notifications
    'notifications.title': 'الإشعارات',
    'notifications.subtitle': 'تابع آخر التحديثات والتنبيهات',
    'notifications.markAllRead': 'تحديد الكل كمقروء',
    'notifications.offerAccepted': 'تم قبول عرضك',
    'notifications.offerAcceptedDesc': 'تم قبول عرضك على مناقصة الهياكل المعدنية',
    'notifications.newTender': 'مناقصة جديدة',
    'notifications.newTenderDesc': 'مناقصة جديدة تناسب تخصصك: توريد حديد التسليح',
    'notifications.deadlineReminder': 'تذكير بالموعد النهائي',
    'notifications.deadlineReminderDesc': 'ينتهي موعد تقديم العروض على مناقصة الاسمنت غداً',
    'notifications.requestRejected': 'تم رفض الطلب',
    'notifications.requestRejectedDesc': 'للأسف تم رفض طلبك على مناقصة البنية التحتية',
    'notifications.timeAgo2h': 'منذ ساعتين',
    'notifications.timeAgo5h': 'منذ 5 ساعات',
    'notifications.timeAgo1d': 'منذ يوم',
    'notifications.timeAgo3d': 'منذ 3 أيام',
    
    // Portfolio
    'portfolio.title': 'معرض الأعمال',
    'portfolio.subtitle': 'نماذج من المشاريع المنفذة عبر منصتنا',
    'portfolio.clientName': 'اسم العميل',
    'portfolio.descriptionAr': 'وصف المشروع (عربي)',
    'portfolio.descriptionEn': 'وصف المشروع (إنجليزي)',
    'portfolio.addProjectBtn': 'إضافة سابقة أعمال',
    'portfolio.updateProjectBtn': 'تحديث المشروع',
    'portfolio.viewDetails': 'عرض التفاصيل',
    'portfolio.edit': 'تعديل',
    'portfolio.delete': 'حذف',
    'portfolio.notApproved': 'حسابك قيد الموافقة',
    'portfolio.notApprovedDesc': 'حسابك قيد الموافقة. يرجى انتظار موافقة المسؤول قبل إضافة أو تعديل المشاريع.',
    'portfolio.approvalRequired': 'الموافقة مطلوبة',
    'portfolio.approvalRequiredDesc': 'يجب أن يكون حسابك موافقاً عليه لإدارة المشاريع.',
    'portfolio.yearHint': 'أدخل السنة فقط (مثال: 2023)',
    'portfolio.projectNotFound': 'المشروع غير موجود',
    'portfolio.projectAddedDesc': 'تم إضافة المشروع بنجاح',
    'portfolio.projectUpdatedDesc': 'تم تحديث المشروع بنجاح',
    'portfolio.projectDeleted': 'تم حذف المشروع',
    'portfolio.projectDeletedDesc': 'تم حذف المشروع بنجاح',
    'portfolio.addFailed': 'فشل الإضافة',
    'portfolio.updateFailed': 'فشل التحديث',
    'portfolio.deleteFailed': 'فشل الحذف',
    
    // Admin Clients
    'admin.clients.title': 'إدارة العملاء',
    'admin.clients.subtitle': 'عرض وإدارة جميع العملاء',
    'admin.clients.adminOnly': 'هذه الصفحة متاحة فقط للمسؤولين',
    'admin.clients.searchPlaceholder': 'البحث عن عميل...',
    'admin.clients.filterStatus': 'فلترة حسب الحالة',
    'admin.clients.allStatuses': 'جميع الحالات',
    'admin.clients.suspended': 'معلق',
    'admin.clients.viewDetails': 'عرض التفاصيل',
    'admin.clients.approve': 'اعتماد',
    'admin.clients.reject': 'رفض',
    'admin.clients.suspend': 'تعليق',
    'admin.clients.delete': 'حذف',
    'admin.clients.clientDetails': 'تفاصيل العميل',
    'admin.clients.clientDetailsDesc': 'معلومات تفصيلية عن العميل',
    'admin.clients.name': 'الاسم',
    'admin.clients.email': 'البريد الإلكتروني',
    'admin.clients.companyName': 'اسم الشركة',
    'admin.clients.phone': 'الهاتف',
    'admin.clients.address': 'العنوان',
    'admin.clients.registrationDate': 'تاريخ التسجيل',
    'admin.clients.clientNotFound': 'العميل غير موجود',
    'admin.clients.noClients': 'لا توجد عملاء',
    'admin.clients.confirmDelete': 'هل أنت متأكد من حذف هذا العميل؟',
    'admin.clients.statusUpdated': 'تم تحديث الحالة',
    'admin.clients.approved': 'تم الاعتماد',
    'admin.clients.rejected': 'تم الرفض',
    'admin.clients.suspendReason': 'سبب التعليق',
    'admin.clients.suspendReasonDesc': 'أدخل سبب تعليق العميل',
    'admin.clients.suspendReasonPlaceholder': 'اكتب سبب التعليق هنا...',
    'admin.clients.deleteFailed': 'فشل الحذف',
    'admin.clients.clientCreated': 'تم إنشاء العميل',
    'admin.clients.clientCreatedDesc': 'تم إنشاء العميل بنجاح',
    'admin.clients.createFailed': 'فشل إنشاء العميل',
    'admin.clients.userInfo': 'معلومات المستخدم',
    'admin.clients.clientInfo': 'معلومات العميل',
    'admin.clients.userType': 'نوع المستخدم',
    'admin.clients.userCreatedAt': 'تاريخ إنشاء المستخدم',
    'admin.clients.clientCreatedAt': 'تاريخ إنشاء العميل',
    'admin.clients.licenseNumber': 'رقم الترخيص',
    'admin.clients.taxId': 'الرقم الضريبي',
    'admin.clients.specializations': 'الاختصاصات',
    'admin.clients.projects': 'المشاريع',
    'admin.clients.reactivate': 'إعادة التفعيل',
    'admin.clients.create': 'إنشاء عميل',
    'admin.clients.createClient': 'إنشاء عميل جديد',
    'admin.clients.createDescription': 'إنشاء حساب عميل جديد في النظام',
    'admin.clients.createClientDesc': 'إضافة عميل جديد إلى النظام',
    'admin.clients.edit': 'تعديل',
    'admin.clients.status': 'الحالة',
    'admin.clients.createdAt': 'تاريخ الإنشاء',
    'admin.clients.actions': 'الإجراءات',
    'admin.clients.overview': 'نظرة عامة',
    'admin.clients.documents': 'الوثائق',
    'admin.clients.noDocuments': 'لا توجد وثائق',
    'admin.clients.noSpecializations': 'لا توجد تخصصات',
    'admin.clients.noProjects': 'لا توجد مشاريع',
    'admin.clients.client': 'عميل',
    'admin.clients.profileComplete': 'اكتمل الملف الشخصي',
    'admin.clients.rejectReason': 'سبب الرفض',
    'admin.clients.rejectReasonDesc': 'أدخل سبب رفض العميل',
    'admin.clients.rejectReasonPlaceholder': 'اكتب سبب الرفض هنا...',
    
    // About
    'about.title': 'عن KYNZ Contracting',
    'about.subtitle': 'منصة رائدة في إدارة المناقصات وربط المقاولين بالفرص المناسبة',
    'about.vision': 'رؤيتنا',
    'about.visionDesc': 'أن نكون المنصة الأولى والأكثر موثوقية في قطاع المقاولات لربط أصحاب المشاريع بالمقاولين والموردين المؤهلين في المنطقة العربية.',
    'about.mission': 'رسالتنا',
    'about.missionDesc': 'تسهيل عملية المناقصات وتوفير بيئة شفافة وعادلة تمكّن الجميع من الوصول إلى الفرص المناسبة وتحقيق النجاح المشترك.',
    'about.values': 'قيمنا',
    'about.quality': 'الجودة',
    'about.qualityDesc': 'نسعى لتقديم أفضل خدمة ممكنة لعملائنا',
    'about.transparency': 'الشفافية',
    'about.transparencyDesc': 'نعمل بكل وضوح ونزاهة في جميع تعاملاتنا',
    'about.innovation': 'الابتكار',
    'about.innovationDesc': 'نطور حلولاً مبتكرة لتلبية احتياجات السوق',
    
    // Contact
    'contact.title': 'تواصل معنا',
    'contact.subtitle': 'نسعد بتلقي استفساراتكم واقتراحاتكم',
    'contact.info': 'معلومات التواصل',
    'contact.phone': 'الهاتف',
    'contact.email': 'البريد الإلكتروني',
    'contact.address': 'العنوان',
    'contact.addressValue': 'المملكة العربية السعودية، الرياض',
    'contact.sendMessage': 'أرسل رسالة',
    'contact.fullName': 'الاسم الكامل',
    'contact.subject': 'الموضوع',
    'contact.message': 'رسالتك...',
    'contact.send': 'إرسال الرسالة',
    'contact.messageSent': 'تم إرسال رسالتك',
    'contact.messageSentDesc': 'سنتواصل معك في أقرب وقت ممكن',
    'contact.error': 'فشل إرسال الرسالة',
    
    // How It Works
    'howitworks.title': 'كيف يعمل الموقع؟',
    'howitworks.subtitle': 'خطوات بسيطة للوصول إلى أفضل الفرص في قطاع المقاولات',
    'howitworks.step1': 'إنشاء حساب',
    'howitworks.step1Desc': 'سجّل حسابك وأكمل ملفك الشخصي مع المستندات الرسمية وتحديد تخصصك',
    'howitworks.step2': 'تصفح المناقصات',
    'howitworks.step2Desc': 'استعرض المناقصات المتاحة المفلترة حسب تخصصك ومجال عملك',
    'howitworks.step3': 'تقديم العروض',
    'howitworks.step3Desc': 'قدّم عرضك مع الملفات المطلوبة وتابع حالة طلبك',
    'howitworks.step4': 'الحصول على الموافقة',
    'howitworks.step4Desc': 'تلقّى إشعاراً فورياً عند قبول عرضك وابدأ العمل',
    'howitworks.startNow': 'ابدأ الآن',
    'howitworks.faq': 'الأسئلة الشائعة',
    'howitworks.faq1': 'هل التسجيل مجاني؟',
    'howitworks.faq1Desc': 'نعم، التسجيل في المنصة مجاني تماماً ولا توجد رسوم مخفية.',
    'howitworks.faq2': 'ما المستندات المطلوبة للتسجيل؟',
    'howitworks.faq2Desc': 'السجل التجاري، شهادة الزكاة والدخل، وشهادات الجودة إن وجدت.',
    'howitworks.faq3': 'كيف أعرف عن المناقصات الجديدة؟',
    'howitworks.faq3Desc': 'ستتلقى إشعارات بالبريد الإلكتروني وداخل المنصة عند نشر مناقصات تناسب تخصصك.',
    
    // Privacy
    'privacy.title': 'سياسة الخصوصية',
    'privacy.collectTitle': 'جمع المعلومات',
    'privacy.collectDesc': 'نقوم بجمع المعلومات التي تقدمها لنا مباشرة عند التسجيل في المنصة، بما في ذلك الاسم والبريد الإلكتروني ورقم الهاتف والمستندات الرسمية.',
    'privacy.useTitle': 'استخدام المعلومات',
    'privacy.useDesc': 'نستخدم المعلومات المجمعة لتقديم خدماتنا وتحسينها، والتواصل معك بشأن المناقصات والتحديثات المهمة.',
    'privacy.protectTitle': 'حماية المعلومات',
    'privacy.protectDesc': 'نتخذ إجراءات أمنية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو الإفصاح أو التعديل.',
    'privacy.shareTitle': 'مشاركة المعلومات',
    'privacy.shareDesc': 'لا نبيع أو نؤجر معلوماتك الشخصية لأطراف ثالثة. قد نشارك المعلومات الضرورية مع الجهات المختصة عند الطلب القانوني.',
    'privacy.contactTitle': 'تواصل معنا',
    'privacy.contactDesc': 'إذا كانت لديك أي أسئلة حول سياسة الخصوصية، يرجى التواصل معنا عبر البريد الإلكتروني: privacy@kynz.com',
    
    // Terms
    'terms.title': 'الشروط والأحكام',
    'terms.acceptTitle': 'القبول بالشروط',
    'terms.acceptDesc': 'باستخدامك لمنصة KYNZ Contracting، فإنك توافق على الالتزام بهذه الشروط والأحكام. إذا كنت لا توافق على أي جزء من هذه الشروط، يرجى عدم استخدام المنصة.',
    'terms.registrationTitle': 'التسجيل والحساب',
    'terms.registrationDesc': 'يجب تقديم معلومات صحيحة ودقيقة عند التسجيل. أنت مسؤول عن الحفاظ على سرية بيانات حسابك وعن جميع الأنشطة التي تتم تحت حسابك.',
    'terms.usageTitle': 'الاستخدام المقبول',
    'terms.usageDesc': 'يجب استخدام المنصة للأغراض المشروعة فقط. يُحظر استخدام المنصة لأي أنشطة غير قانونية أو احتيالية.',
    'terms.offersTitle': 'المناقصات والعروض',
    'terms.offersDesc': 'جميع العروض المقدمة ملزمة وتخضع لمراجعة الإدارة. تحتفظ المنصة بحق رفض أي عرض دون إبداء الأسباب.',
    'terms.disclaimerTitle': 'إخلاء المسؤولية',
    'terms.disclaimerDesc': 'المنصة ليست مسؤولة عن أي خسائر أو أضرار ناتجة عن استخدام الخدمة أو عدم القدرة على استخدامها.',
    'terms.modificationsTitle': 'التعديلات',
    'terms.modificationsDesc': 'نحتفظ بالحق في تعديل هذه الشروط في أي وقت. سيتم إخطارك بأي تغييرات جوهرية عبر البريد الإلكتروني أو إشعار داخل المنصة.',
    
    // NotFound
    'notfound.title': '404',
    'notfound.message': 'Oops! Page not found',
    'notfound.backHome': 'Return to Home',
    
    // Common
    'common.search': 'بحث',
    'common.order': 'الترتيب',
    'common.title': 'العنوان',
    'common.titleEn': 'العنوان (إنجليزي)',
    'common.titleAr': 'العنوان (عربي)',
    'common.descriptionEn': 'الوصف (إنجليزي)',
    'common.descriptionAr': 'الوصف (عربي)',
    'common.locationEn': 'الموقع (إنجليزي)',
    'common.locationAr': 'الموقع (عربي)',
    'common.client': 'العميل',
    'common.images': 'الصور',
    'common.completedDate': 'تاريخ الإنجاز',
    'common.allStatuses': 'جميع الحالات',
    'common.deleteConfirm': 'تأكيد الحذف',
    'common.manageAccount': 'إدارة إعدادات حسابك',
    'common.emailNotifications': 'إشعارات البريد الإلكتروني',
    'common.emailNotificationsDesc': 'استلام الإشعارات عبر البريد',
    'common.smsNotifications': 'إشعارات الرسائل النصية',
    'common.smsNotificationsDesc': 'استلام الإشعارات عبر SMS',
    'common.newTenderAlerts': 'تنبيهات المناقصات الجديدة',
    'common.newTenderAlertsDesc': 'إشعار عند نشر مناقصة تناسب تخصصك',
    'common.applicationUpdates': 'تحديثات الطلبات',
    'common.applicationUpdatesDesc': 'إشعار عند تحديث حالة طلباتك',
    'common.emailServerDesc': 'إعدادات خادم البريد الإلكتروني لإرسال الإشعارات',
    'common.usernamePlaceholder': 'اسم المستخدم',
    'common.senderNamePlaceholder': 'اسم المرسل',
    'common.loading': 'جاري المعالجة...',
    'common.cancel': 'إلغاء',
    'common.save': 'حفظ',
    'common.selectIcon': 'اختر الأيقونة',
    'common.searchIcons': 'ابحث عن أيقونة...',
    'common.noIconsFound': 'لم يتم العثور على أيقونات',
    'common.selectedIcon': 'الأيقونة المختارة',
    'common.status': 'الحالة',
    'common.optional': 'اختياري',
    'common.active': 'نشط',
    'common.inactive': 'غير نشط',
    'common.activate': 'تفعيل',
    'common.deactivate': 'إلغاء التفعيل',
    'common.closed': 'مغلق',
    'common.view': 'عرض',
    'common.edit': 'تعديل',
    'common.delete': 'حذف',
    'common.create': 'إنشاء',
    'common.update': 'تحديث',
    'common.actions': 'الإجراءات',
    'common.submitting': 'جاري التقديم...',
    'common.add': 'إضافة',
    'common.creating': 'جاري الإنشاء...',
    'common.deleting': 'جاري الحذف...',
    'common.selectOptions': 'اختر الخيارات...',
    'common.clearAll': 'مسح الكل',
    'common.noOptionsFound': 'لم يتم العثور على خيارات',
    'common.remove': 'إزالة',
    
    // Index Projects
    'index.projects.title': 'أحدث المشاريع',
    'index.projects.subtitle': 'نماذج من المشاريع المنفذة عبر منصتنا',
    'index.projects.adminTitle': 'المشاريع العامة',
    'index.projects.adminSubtitle': 'معاينة المشاريع العامة المعروضة على المنصة',
    'index.projects.viewAllAdmin': 'عرض جميع المشاريع',
    'index.projects.riyadhTowers': 'مشروع أبراج الرياض',
    'index.projects.riyadhTowersCategory': 'حديد وهياكل معدنية',
    'index.projects.cementFactory': 'مصنع الاسمنت الحديث',
    'index.projects.cementFactoryCategory': 'مواد بناء',
    'index.projects.industrialComplex': 'مجمع الصناعات',
    'index.projects.industrialComplexCategory': 'هياكل صناعية',
    'index.features.subtitle': 'منصة متكاملة تجمع بين سهولة الاستخدام والاحترافية العالية',
    
    // Portfolio Projects
    'portfolio.project.riyadhTowers': 'مشروع أبراج الرياض',
    'portfolio.project.riyadhTowersDesc': 'توريد وتركيب الهياكل الحديدية لمجمع أبراج تجارية',
    'portfolio.project.cementFactory': 'مصنع الاسمنت الحديث',
    'portfolio.project.cementFactoryDesc': 'توريد معدات ومواد بناء لمصنع اسمنت متكامل',
    'portfolio.project.industrialComplex': 'مجمع الصناعات',
    'portfolio.project.industrialComplexDesc': 'تصميم وتنفيذ هياكل صناعية لمجمع متعدد الاستخدامات',
    'portfolio.project.smartCity': 'مشروع المدينة الذكية',
    'portfolio.project.smartCityDesc': 'أعمال البنية التحتية لمشروع المدينة الذكية',
    'portfolio.project.warehouseComplex': 'مجمع المستودعات',
    'portfolio.project.warehouseComplexDesc': 'بناء مجمع مستودعات بمساحة 50,000 متر مربع',
    'portfolio.project.powerStation': 'محطة الطاقة',
    'portfolio.project.powerStationDesc': 'توريد وتركيب معدات محطة توليد الطاقة',
    'portfolio.category.steel': 'حديد وهياكل معدنية',
    'portfolio.category.buildingMaterials': 'مواد بناء',
    'portfolio.category.industrialStructures': 'هياكل صناعية',
    'portfolio.category.infrastructure': 'بنية تحتية',
    'portfolio.category.construction': 'إنشاءات',
    'portfolio.category.energy': 'طاقة',
    'portfolio.newProjectDetailsDesc': 'إضافة تفاصيل المشروع',
    'portfolio.deleteWarning': 'هل أنت متأكد من حذف هذا المشروع؟ لا يمكن التراجع عن هذا الإجراء.',
    'portfolio.projectNotFoundDesc': 'لم نتمكن من العثور على هذا المشروع',
    'portfolio.backToProjects': 'العودة إلى المشاريع',
    'portfolio.professionalExecution': 'تنفيذ احترافي',
    'portfolio.requestSimilar': 'اطلب مشروع مشابه',
    'profile.status': 'الحالة',
    'profile.createdAt': 'تاريخ الإنشاء',
    'profile.updatedAt': 'تاريخ التحديث',
    
    // Client Portfolio
    'clientPortfolio.project.kingAbdullahTower': 'مشروع برج الملك عبدالله',
    'clientPortfolio.project.kingAbdullahTowerDesc': 'توريد 300 طن حديد تسليح عالي الجودة',
    'clientPortfolio.project.oasisResidential': 'مجمع سكني الواحة',
    'clientPortfolio.project.oasisResidentialDesc': 'توريد مواد بناء متنوعة للمشروع',
    'clientPortfolio.project.desalinationPlant': 'محطة تحلية المياه',
    'clientPortfolio.project.desalinationPlantDesc': 'توريد اسمنت مقاوم للأملاح',
    'clientPortfolio.client.advancedConstruction': 'شركة البناء المتقدم',
    'clientPortfolio.client.imarGroup': 'مجموعة الإعمار',
    'clientPortfolio.client.nationalWater': 'شركة المياه الوطنية',
    
    // Tenders Public
    'tenders.public.steelSupply': 'مناقصة توريد حديد التسليح',
    'tenders.public.paintWorks': 'مناقصة توريد وتركيب أعمال الدهان',
    'tenders.public.cementSupply': 'مناقصة توريد الاسمنت',
    'tenders.public.metalStructures': 'مناقصة أعمال الهياكل المعدنية',
    'tenders.public.deadline': 'الموعد النهائي',
    'tenders.available': 'المناقصات المتاحة',
    
    // Dashboard User Name
    'dashboard.userName': 'أحمد',
    
    
    // Sidebar Keys
    'sidebar.offers': 'العروض',
    
    // Admin Offers Keys
    'admin.offers.offerDetails': 'تفاصيل العرض',
    'admin.offers.offerInfo': 'معلومات العرض',
    'admin.offers.relatedTender': 'المناقصة المرتبطة',
    'admin.offers.proposedAmount': 'المبلغ المقترح',
    'admin.offers.notes': 'ملاحظات',
    'admin.offers.submittedAt': 'تاريخ التقديم',
    'admin.offers.offerId': 'رقم العرض',
    'admin.offers.clientInfo': 'معلومات العميل',
    'admin.offers.clientStatus': 'حالة العميل',
    'admin.offers.quickActions': 'إجراءات سريعة',
    'admin.offers.viewTender': 'عرض المناقصة',
    'admin.offers.viewClient': 'عرض العميل',
    'admin.offers.accepted': 'مقبول',
    'admin.offers.rejected': 'مرفوض',
    'admin.offers.pending': 'معلق',
    'admin.offers.accept': 'قبول',
    'admin.offers.reject': 'رفض',
    'admin.offers.acceptConfirm': 'قبول العرض',
    'admin.offers.acceptConfirmDesc': 'هل أنت متأكد من قبول هذا العرض؟ سيتم إعلام العميل ولا يمكن التراجع عن هذا الإجراء.',
    'admin.offers.rejectConfirm': 'رفض العرض',
    'admin.offers.rejectConfirmDesc': 'هل أنت متأكد من رفض هذا العرض؟ سيتم إعلام العميل ولا يمكن التراجع عن هذا الإجراء.',
    'admin.offers.acceptSuccess': 'تم قبول العرض بنجاح',
    'admin.offers.acceptSuccessDesc': 'تم قبول العرض وسيتم إعلام العميل.',
    'admin.offers.rejectSuccess': 'تم رفض العرض بنجاح',
    'admin.offers.rejectSuccessDesc': 'تم رفض العرض وسيتم إعلام العميل.',
    'admin.offers.acceptError': 'فشل في قبول العرض',
    'admin.offers.acceptErrorDesc': 'حدث خطأ أثناء قبول العرض.',
    'admin.offers.rejectError': 'فشل في رفض العرض',
    'admin.offers.rejectErrorDesc': 'حدث خطأ أثناء رفض العرض.',
    'admin.offers.offerNotFound': 'العرض غير موجود',
    'admin.offers.offerNotFoundDesc': 'العرض الذي تبحث عنه غير موجود أو تم حذفه.',
    'admin.offers.attachments': 'المرفقات',
    'admin.offers.email': 'البريد الإلكتروني',
    'admin.offers.phone': 'رقم الهاتف',
    'admin.offers.address': 'العنوان',
    
    // Admin Company Projects
    'admin.companyProjects': 'مشاريع الشركة',
    'admin.companyProjectsDesc': 'إدارة مشاريع الشركة المعروضة في الموقع',
    'admin.companyProjects.add': 'إضافة مشروع',
    'admin.companyProjects.edit': 'تعديل المشروع',
    'admin.companyProjects.delete': 'حذف المشروع',
    'admin.companyProjects.noProjects': 'لا توجد مشاريع',
    'admin.companyProjects.created': 'تم إنشاء المشروع',
    'admin.companyProjects.createdDesc': 'تم إنشاء المشروع بنجاح',
    'admin.companyProjects.updated': 'تم تحديث المشروع',
    'admin.companyProjects.updatedDesc': 'تم تحديث المشروع بنجاح',
    'admin.companyProjects.deleted': 'تم حذف المشروع',
    'admin.companyProjects.deletedDesc': 'تم حذف المشروع بنجاح',
    'admin.companyProjects.location': 'الموقع',
    'admin.companyProjects.completedDate': 'تاريخ الإنجاز',
    'admin.companyProjects.isActive': 'نشط',
    'admin.companyProjects.images': 'الصور',
    'admin.companyProjects.uploadImages': 'رفع صور',
    'admin.companyProjects.titleEn': 'العنوان (إنجليزي)',
    'admin.companyProjects.titleAr': 'العنوان (عربي)',
    'admin.companyProjects.descriptionEn': 'الوصف (إنجليزي)',
    'admin.companyProjects.descriptionAr': 'الوصف (عربي)',
    'admin.companyProjects.locationEn': 'الموقع (إنجليزي)',
    'admin.companyProjects.locationAr': 'الموقع (عربي)',
    
    // Admin Client Projects
    'admin.clientProjects': 'مشاريع العملاء',
    'admin.clientProjectsDesc': 'إدارة مشاريع العملاء والتحكم في حالتها',
    'admin.clientProjects.noProjects': 'لا توجد مشاريع',
    'admin.clientProjects.status': 'الحالة',
    'admin.clientProjects.statusPending': 'قيد المراجعة',
    'admin.clientProjects.statusApproved': 'معتمد',
    'admin.clientProjects.statusCompleted': 'مكتمل',
    'admin.clientProjects.statusRejected': 'مرفوض',
    
    
    // Sidebar Specializations
    'sidebar.specializations': 'التخصصات',
    
    // Tenders Keys
    'tenders.changeStatus': 'تغيير الحالة',
    
    // Specializations Page
    'specializations.title': 'التخصصات',
    'specializations.subtitle': 'إدارة تخصصات المشروع والفئات',
    'specializations.addSpecialization': 'إضافة تخصص',
    'specializations.allSpecializations': 'جميع التخصصات',
    'specializations.listDescription': 'قائمة بجميع التخصصات في النظام',
    'specializations.searchPlaceholder': 'بحث عن التخصصات...',
    'specializations.actions': 'الإجراءات',
    'specializations.description': 'الوصف',
    'specializations.status': 'الحالة',
    'specializations.created': 'تاريخ الإنشاء',
    'specializations.active': 'نشط',
    'specializations.inactive': 'غير نشط',
    'specializations.name': 'اسم',
    'specializations.loading': 'جارِ التحميل...',
    'specializations.createTitle':'إضافة تخصص',
    'specializations.createDesc':'إضافة تخصص جديد للنظام',
    'specializations.englishName':'اسم التخصص باللغة الإنجليزية',
    'specializations.englishNamePlaceholder':'أدخل اسم التخصص باللغة الإنجليزية',
    'specializations.arabicName':'اسم التخصص باللغة العربية',
    'specializations.arabicNamePlaceholder':'أدخل اسم التخصص باللغة العربية',
    'specializations.englishDescription':'وصف التخصص باللغة الإنجليزية',
    'specializations.englishDescriptionPlaceholder':'أدخل وصف التخصص باللغة الإنجليزية',
    'specializations.arabicDescription':'وصف التخصص باللغة العربية',
    'specializations.arabicDescriptionPlaceholder':'أدخل وصف التخصص باللغة العربية',
    'specializations.activeDesc':'مكتمل',
    'specializations.cancel':'إلغاء',
    'specializations.updateButton':'تحديث',
    'specializations.createButton': 'إنشاء تخصص',
    'specializations.editTitle':'تحرير التخصص',
    'specializations.editDesc':'تحرير التخصص',




    // Admin Settings
    'settings.adminTitle': 'إعدادات الأدمن',
    'settings.adminSubtitle': 'إدارة الإعدادات والتكوينات على مستوى النظام',
    'settings.languageSettings': 'إعدادات اللغة',
    'settings.languageDesc': 'تعيين اللغة الافتراضية للمنصة',
    'settings.storageSettings': 'إعدادات التخزين',
    'settings.storageDesc': 'تكوين إعدادات التخزين ورفع الملفات',
    'settings.emailSettings': 'إعدادات البريد الإلكتروني',
    'settings.emailDesc': 'تكوين إعدادات إرسال البريد الإلكتروني',
    'settings.s3Settings': 'إعدادات Amazon S3',
    'settings.s3Desc': 'تكوين إعدادات تخزين Amazon S3',
    'settings.storageDisk': 'قرص التخزين',
    'settings.serverStorage': 'تخزين داخل الخادم',
    'settings.externalStorage': 'خدمة تخزين خارجية (Amazon S3)',
    'settings.maxFileSizeKB': 'الحد الأقصى لحجم الملف (KB)',
    'settings.allowedFileTypes': 'أنواع الملفات المسموحة',
    'settings.saveStorageSettings': 'حفظ إعدادات التخزين',
    'settings.saveEmailSettings': 'حفظ إعدادات البريد الإلكتروني',
    'settings.saveS3Settings': 'حفظ إعدادات S3',
    'settings.saving': 'جاري الحفظ...',
    'settings.mailerService': 'خدمة البريد',
    'settings.host': 'المضيف',
    'settings.port': 'المنفذ',
    'settings.encryption': 'التشفير',
    'settings.username': 'اسم المستخدم',
    'settings.password': 'كلمة المرور',
    'settings.fromAddress': 'البريد المرسل منه',
    'settings.fromName': 'اسم المرسل',
    'settings.accessKeyId': 'معرف مفتاح الوصول',
    'settings.secretAccessKey': 'مفتاح الوصول السري',
    'settings.region': 'المنطقة',
    'settings.bucketName': 'اسم الحاوية',
    'settings.url': 'الرابط',
    'settings.endpoint': 'نقطة النهاية',
    'settings.usePathStyleEndpoint': 'استخدام نقطة نهاية نمط المسار',
    'settings.storageUpdated': 'تم تحديث إعدادات التخزين',
    'settings.storageUpdatedDesc': 'تم تحديث إعدادات التخزين بنجاح',
    'settings.emailUpdated': 'تم تحديث إعدادات البريد الإلكتروني',
    'settings.emailUpdatedDesc': 'تم تحديث إعدادات البريد الإلكتروني بنجاح',
    'settings.s3Updated': 'تم تحديث إعدادات S3',
    'settings.s3UpdatedDesc': 'تم تحديث إعدادات S3 بنجاح',
    'settings.storageUpdateError': 'فشل في تحديث إعدادات التخزين',
    'settings.emailUpdateError': 'فشل في تحديث إعدادات البريد الإلكتروني',
    'settings.s3UpdateError': 'فشل في تحديث إعدادات S3',
    
    // Account Status
    'accountStatus.pending.title': 'الحساب قيد المراجعة',
    'accountStatus.pending.description': 'حسابك قيد المراجعة حالياً. يمكنك عرض بياناتك ولكن لا يمكنك إجراء تغييرات حتى الموافقة.',
    'accountStatus.pending.canDo': 'يمكنك عرض المشاريع الحالية والمناقصات والسجل.',
    'accountStatus.pending.restoreAccess': 'يرجى انتظار موافقة الإدارة. تحقق من بريدك الإلكتروني للحصول على التحديثات.',
    'accountStatus.pending.welcome': 'الحساب قيد المراجعة',
    'accountStatus.pending.welcomeMessage': 'حسابك قيد المراجعة. يمكنك عرض بياناتك ولكن لا يمكنك إجراء تغييرات حتى الموافقة.',
    
    'accountStatus.suspended.title': 'الحساب معلق مؤقتاً',
    'accountStatus.suspended.description': 'تم تعليق حسابك مؤقتاً. يمكنك عرض بياناتك ولكن لا يمكنك إجراء تغييرات.',
    'accountStatus.suspended.canDo': 'يمكنك عرض بياناتك والتواصل مع الدعم للحصول على المساعدة.',
    'accountStatus.suspended.restoreAccess': 'تواصل مع الدعم لحل مشكلة التعليق.',
    'accountStatus.suspended.welcome': 'الحساب معلق',
    'accountStatus.suspended.welcomeMessage': 'تم تعليق حسابك مؤقتاً. يمكنك عرض بياناتك ولكن لا يمكنك إجراء تغييرات.',
    
    'accountStatus.rejected.title': 'الحساب مرفوض',
    'accountStatus.rejected.description': 'تم رفض حسابك بشكل دائم. يمكنك فقط عرض بياناتك وتصديرها.',
    'accountStatus.rejected.canDo': 'يمكنك عرض بياناتك وطلب تصدير البيانات.',
    'accountStatus.rejected.restoreAccess': 'تواصل مع الإدارة إذا كنت تعتقد أن هذا خطأ.',
    'accountStatus.rejected.welcome': 'الحساب مرفوض',
    'accountStatus.rejected.welcomeMessage': 'تم رفض حسابك بشكل دائم. يمكنك فقط عرض بياناتك وتصديرها.',
    
    'accountStatus.default.title': 'حالة الحساب',
    'accountStatus.default.description': 'حالة حسابك غير نشطة.',
    'accountStatus.default.canDo': 'يمكنك عرض بياناتك الموجودة.',
    'accountStatus.default.restoreAccess': 'تواصل مع الدعم للمساعدة.',
    
    'accountStatus.reason': 'السبب',
    'accountStatus.date': 'التاريخ',
    'accountStatus.whatYouCanDo': 'ما يمكنك فعله',
    'accountStatus.restoreAccess': 'كيفية استعادة الوصول',
    'accountStatus.contactSupport': 'تواصل مع الدعم',
    
    // Read Only Mode
    'readOnly.pending': 'الإجراء غير متاح أثناء مراجعة الحساب',
    'readOnly.suspended': 'الإجراء غير متاح أثناء تعليق الحساب',
    'readOnly.rejected': 'الإجراء غير متاح للحسابات المرفوضة',
    'readOnly.default': 'الإجراء غير متاح في حالة الحساب الحالية',
    
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.tenders': 'Tenders',
    'nav.howItWorks': 'How It Works',
    'nav.portfolio': 'Portfolio',
    'nav.contact': 'Contact',
    'nav.login': 'Login',
    'nav.register': 'Register',
    'nav.profile': 'Profile',
    'nav.settings': 'Settings',
    'nav.logout': 'Logout',
    
    // Footer
    'footer.description': 'An integrated platform for managing tenders and projects in the contracting and construction sector',
    'footer.quickLinks': 'Quick Links',
    'footer.legal': 'Legal',
    'footer.privacy': 'Privacy Policy',
    'footer.terms': 'Terms & Conditions',
    'footer.contact': 'Contact Us',
    'footer.rights': 'All rights reserved',
    'footer.viewLocation': 'View Location',
    'footer.noLinks': 'No links available',
    
    // Sidebar
    'sidebar.dashboard': 'Data Panel',
    'sidebar.profile': 'Profile',
    'sidebar.portfolio': 'Portfolio',
    'sidebar.tenders': 'Tenders',
    'sidebar.applications': 'Applications',
    'sidebar.notifications': 'Notifications',
    'sidebar.settings': 'Settings',
    'sidebar.clients': 'Manage Clients',
    'sidebar.contentManagement': 'Content Management',
    'sidebar.logout': 'Logout',
    
    // Content Management
    'content.title': 'Content Management',
    'content.subtitle': 'Manage platform content including homepage, about, how it works, FAQs, contact, navigation, and footer',
    'content.tabs.homepage': 'Homepage',
    'content.tabs.about': 'About',
    'content.tabs.howItWorks': 'How It Works',
    'content.tabs.faqs': 'FAQs',
    'content.tabs.contact': 'Contact',
    'content.tabs.navigation': 'Navigation',
    'content.tabs.footer': 'Footer',
    
    // Homepage Content
    'content.homepage.title': 'Homepage Content',
    'content.homepage.description': 'Manage homepage content',
    'content.homepage.edit': 'Edit Content',
    'content.homepage.noContent': 'No content available. Click Edit Content to add new content.',
    'content.homepage.features': 'Features',
    'content.homepage.featuresDescription': 'Manage homepage features',
    'content.homepage.addFeature': 'Add Feature',
    'content.homepage.noFeatures': 'No features available. Click Add Feature to add a new feature.',
    'content.homepage.statistics': 'Statistics',
    'content.homepage.statisticsDescription': 'Manage homepage statistics',
    'content.homepage.addStatistic': 'Add Statistic',
    'content.homepage.noStatistics': 'No statistics available. Click Add Statistic to add a new statistic.',
    'content.homepage.updated': 'Homepage content updated',
    'content.homepage.updatedDesc': 'Homepage content has been updated successfully',
    'content.homepage.editDescription': 'Edit homepage content',
    'content.homepage.mainTitle': 'Main Title',
    'content.homepage.mainDescription': 'Main Description',
    'content.homepage.mainTitleEn': 'Main Title (English)',
    'content.homepage.mainTitleAr': 'Main Title (Arabic)',
    'content.homepage.heroTitleColor': 'Background Title Color',
    'content.homepage.heroDescriptionEn': 'Background Description (English)',
    'content.homepage.mainDescriptionAr': 'Main Description (Arabic)',
    'content.homepage.mainCtaTextEn': 'Main CTA Text (English)',
    'content.homepage.mainCtaTextAr': 'Main CTA Text (Arabic)',
    'content.homepage.mainCtaUrl': 'Main CTA URL',
    'content.homepage.sectionSubtitleEn': 'Section Subtitle (English)',
    'content.homepage.sectionSubtitleAr': 'Section Subtitle (Arabic)',
    'content.homepage.sectionSubtitle': 'Section Subtitle',
    'content.homepage.mainCtaText': 'Main CTA Text',
    'content.homepage.mainBackgroundImage': 'Main Background Image',
    'content.homepage.heroTitle': 'Background Title',
    'content.homepage.heroDescription': 'Background Description',
    'content.homepage.heroCtaText': 'Background CTA Text',
    'content.homepage.heroCtaUrl': 'Background CTA URL',
    
    // About Content
    'content.about.title': 'About Page Content',
    'content.about.description': 'Manage about page content',
    'content.about.edit': 'Edit Content',
    'content.about.noContent': 'No content available. Click Edit Content to add new content.',
    'content.about.values': 'Values',
    'content.about.valuesDescription': 'Manage company values',
    'content.about.addValue': 'Add Value',
    'content.about.noValues': 'No values available. Click Add Value to add a new value.',
    'content.about.updated': 'About page content updated',
    'content.about.updatedDesc': 'About page content has been updated successfully',
    'content.about.mainTitle': 'Main Title',
    'content.about.mainTitleEn': 'Main Title (English)',
    'content.about.mainTitleAr': 'Main Title (Arabic)',
    'content.about.heroTitleEn': 'Main Title (English)',
    'content.about.heroTitleAr': 'Main Title (Arabic)',
    'content.about.mainSubtitleEn': 'Main Subtitle (English)',
    'content.about.mainSubtitleAr': 'Main Subtitle (Arabic)',
    'content.about.heroSubtitleEn': 'Main Subtitle (English)',
    'content.about.heroSubtitleAr': 'Main Subtitle (Arabic)',
    'content.about.heroTitle': 'Background Title',
    'content.about.heroSubtitle': 'Background Subtitle',
    'content.about.visionTitle': 'Vision Title',
    'content.about.visionTitleEn': 'Vision Title (English)',
    'content.about.visionTitleAr': 'Vision Title (Arabic)',
    'content.about.visionContentEn': 'Vision Content (English)',
    'content.about.visionContentAr': 'Vision Content (Arabic)',
    'content.about.visionIcon': 'Vision Icon',
    'content.about.missionTitle': 'Mission Title',
    'content.about.missionTitleEn': 'Mission Title (English)',
    'content.about.missionTitleAr': 'Mission Title (Arabic)',
    'content.about.missionContentEn': 'Mission Content (English)',
    'content.about.missionContentAr': 'Mission Content (Arabic)',
    'content.about.missionIcon': 'Mission Icon',
    'content.about.valuesSectionTitleEn': 'Values Section Title (English)',
    'content.about.valuesSectionTitleAr': 'Values Section Title (Arabic)',
    
    'content.value.created': 'Value created',
    'content.value.createdDesc': 'Value has been created successfully',
    'content.value.updated': 'Value updated',
    'content.value.updatedDesc': 'Value has been updated successfully',
    'content.value.deleted': 'Value deleted',
    'content.value.deletedDesc': 'Value has been deleted successfully',
    'content.value.add': 'Add Value',
    'content.value.edit': 'Edit Value',
    'content.value.order': 'Order',
    'content.value.icon': 'Icon',
    'content.value.titleEn': 'Title (English)',
    'content.value.titleAr': 'Title (Arabic)',
    'content.value.contentEn': 'Content (English)',
    'content.value.contentAr': 'Content (Arabic)',
    'content.value.confirmDelete': 'Are you sure you want to delete this value?',
    'content.value.title': 'Title',
    'content.value.content': 'Content',
    'content.value.status': 'Status',
    
    // How It Works Content
    'content.howItWorks.title': 'How It Works Page Content',
    'content.howItWorks.description': 'Manage how it works page content',
    'content.howItWorks.edit': 'Edit Content',
    'content.howItWorks.noContent': 'No content available. Click Edit Content to add new content.',
    'content.howItWorks.steps': 'Steps',
    'content.howItWorks.stepsDescription': 'Manage how it works steps',
    'content.howItWorks.addStep': 'Add Step',
    'content.howItWorks.noSteps': 'No steps available. Click Add Step to add a new step.',
    'content.howItWorks.updated': 'How It Works page content updated',
    'content.howItWorks.updatedDesc': 'How It Works page content has been updated successfully',
    'content.howItWorks.mainTitle': 'Main Title',
    'content.howItWorks.mainTitleEn': 'Main Title (English)',
    'content.howItWorks.mainTitleAr': 'Main Title (Arabic)',
    'content.howItWorks.mainSubtitle': 'Main Subtitle',
    'content.howItWorks.mainSubtitleEn': 'Main Subtitle (English)',
    'content.howItWorks.mainSubtitleAr': 'Main Subtitle (Arabic)',
    'content.howItWorks.ctaButtonTextEn': 'CTA Button Text (English)',
    'content.howItWorks.ctaButtonTextAr': 'CTA Button Text (Arabic)',
    'content.howItWorks.ctaButtonUrl': 'CTA Button URL',
    'content.howItWorks.heroTitle': 'Background Title',
    'content.howItWorks.heroSubtitle': 'Background Subtitle',
    'content.howItWorks.ctaButtonText': 'CTA Button Text',
    
    // FAQs Content
    'content.faqs.title': 'FAQs',
    'content.faqs.description': 'Manage frequently asked questions',
    'content.faqs.add': 'Add FAQ',
    'content.faqs.noFaqs': 'No FAQs available. Click Add FAQ to add a new question.',
    
    // Contact Content
    'content.contact.title': 'Contact Page Content',
    'content.contact.description': 'Manage contact page content',
    'content.contact.edit': 'Edit Content',
    'content.contact.noContent': 'No content available. Click Edit Content to add new content.',
    'content.contact.updated': 'Contact page content updated',
    'content.contact.updatedDesc': 'Contact page content has been updated successfully',
    'content.contact.mainTitle': 'Main Title',
    'content.contact.mainTitleEn': 'Main Title (English)',
    'content.contact.mainTitleAr': 'Main Title (Arabic)',
    'content.contact.heroTitleEn': 'Main Title (English)',
    'content.contact.heroTitleAr': 'Main Title (Arabic)',
    'content.contact.mainSubtitleEn': 'Main Subtitle (English)',
    'content.contact.mainSubtitleAr': 'Main Subtitle (Arabic)',
    'content.contact.heroSubtitleEn': 'Main Subtitle (English)',
    'content.contact.heroSubtitleAr': 'Main Subtitle (Arabic)',
    'content.contact.heroImage': 'Hero Image',
    'content.contact.heroTitle': 'Background Title',
    'content.contact.phone': 'Phone',
    'content.contact.email': 'Email',
    'content.contact.addressEn': 'Address (English)',
    'content.contact.addressAr': 'Address (Arabic)',
    'content.contact.formTitleEn': 'Form Title (English)',
    'content.contact.formTitleAr': 'Form Title (Arabic)',
    'content.contact.formNameLabelEn': 'Name Label (English)',
    'content.contact.formNameLabelAr': 'Name Label (Arabic)',
    'content.contact.formEmailLabelEn': 'Email Label (English)',
    'content.contact.formEmailLabelAr': 'Email Label (Arabic)',
    'content.contact.formPhoneLabelEn': 'Phone Label (English)',
    'content.contact.formPhoneLabelAr': 'Phone Label (Arabic)',
    'content.contact.formSubjectLabelEn': 'Subject Label (English)',
    'content.contact.formSubjectLabelAr': 'Subject Label (Arabic)',
    'content.contact.formMessageLabelEn': 'Message Label (English)',
    'content.contact.formMessageLabelAr': 'Message Label (Arabic)',
    'content.contact.formSubmitButtonTextEn': 'Submit Button Text (English)',
    'content.contact.formSubmitButtonTextAr': 'Submit Button Text (Arabic)',
    'content.contact.phoneIcon': 'Phone Icon',
    'content.contact.emailIcon': 'Email Icon',
    'content.contact.addressIcon': 'Address Icon',
    
    // Navigation Content
    'content.navigation.title': 'Navigation Links',
    'content.navigation.description': 'Manage main menu links',
    'content.navigation.add': 'Add Link',
    'content.navigation.noLinks': 'No links available. Click Add Link to add a new link.',
    
    // Footer Content
    'content.footer.title': 'Footer Content',
    'content.footer.description': 'Manage footer content',
    'content.footer.edit': 'Edit Content',
    'content.footer.noContent': 'No content available. Click Edit Content to add new content.',
    'content.footer.updated': 'Footer content updated',
    'content.footer.updatedDesc': 'Footer content has been updated successfully',
    'content.footer.companyDescription': 'Company Description',
    'content.footer.companyDescriptionEn': 'Company Description (English)',
    'content.footer.companyDescriptionAr': 'Company Description (Arabic)',
    'content.footer.phone': 'Phone',
    'content.footer.email': 'Email',
    'content.footer.copyrightText': 'Copyright Text',
    'content.footer.quickLinksTitleEn': 'Quick Links Title (English)',
    'content.footer.quickLinksTitleAr': 'Quick Links Title (Arabic)',
    'content.footer.legalTitleEn': 'Legal Title (English)',
    'content.footer.legalTitleAr': 'Legal Title (Arabic)',
    'content.footer.contactTitleEn': 'Contact Title (English)',
    'content.footer.contactTitleAr': 'Contact Title (Arabic)',
    'content.footer.locationEn': 'Location (English)',
    'content.footer.locationAr': 'Location (Arabic)',
    'content.footer.companyLogo': 'Company Logo',
    'content.footer.quickLinksTitle': 'Quick Links Title',
    'content.footer.legalTitle': 'Legal Title',
    'content.footer.contactTitle': 'Contact Title',
    'content.footer.location': 'Location',
    'content.footer.status': 'Status',
    'content.footer.createdAt': 'Created At',
    'content.footer.updatedAt': 'Updated At',
    'content.footer.logoHint': 'Choose logo image (PNG, JPG, WEBP)',
    
    // Site Settings Notifications
    'settings.notifications.updated': 'Settings updated',
    'settings.notifications.updatedDesc': 'Site settings have been updated successfully.',
    'settings.notifications.updateFailed': 'Failed to update settings.',
    'settings.notifications.logoUploaded': 'Logo uploaded',
    'settings.notifications.logoUploadedDesc': 'Logo has been uploaded successfully.',
    'settings.notifications.logoUploadFailed': 'Failed to upload logo.',
    'settings.notifications.darkLogoUploaded': 'Dark logo uploaded',
    'settings.notifications.darkLogoUploadedDesc': 'Dark logo has been uploaded successfully.',
    'settings.notifications.darkLogoUploadFailed': 'Failed to upload dark logo.',
    'settings.notifications.faviconUploaded': 'Favicon uploaded',
    'settings.notifications.faviconUploadedDesc': 'Favicon has been uploaded successfully.',
    'settings.notifications.faviconUploadFailed': 'Failed to upload favicon.',
    'settings.notifications.logoDeleted': 'Logo deleted',
    'settings.notifications.logoDeletedDesc': 'Logo has been deleted successfully.',
    'settings.notifications.logoDeleteFailed': 'Failed to delete logo.',
    
    // Tenders Notifications
    'tenders.notifications.offerSubmitted': 'Offer submitted successfully',
    'tenders.notifications.offerSubmittedDesc': 'Your offer will be reviewed and we will contact you soon',
    'tenders.notifications.offerFailed': 'Failed to submit offer',
    'tenders.notifications.offerFailedDesc': 'An unexpected error occurred',
    
    // Profile Notifications
    'profile.notifications.updated': 'Profile updated',
    'profile.notifications.updatedDesc': 'Changes saved successfully',
    'profile.notifications.updateFailed': 'Update failed',
    'profile.notifications.updateFailedDesc': 'An unexpected error occurred',
    'profile.notifications.documentsUploaded': 'Documents uploaded',
    'profile.notifications.documentsUploadedDesc': 'Documents have been uploaded successfully',
    'profile.notifications.documentsUploadFailed': 'Failed to upload documents',
    'profile.notifications.documentsUploadFailedDesc': 'An unexpected error occurred',
    'profile.notifications.documentDeleted': 'Document deleted',
    'profile.notifications.documentDeletedDesc': 'Document has been deleted successfully',
    'profile.notifications.documentDeleteFailed': 'Failed to delete document',
    'profile.notifications.documentDeleteFailedDesc': 'An unexpected error occurred',
    'profile.notifications.imageUpdated': 'Profile updated',
    'profile.notifications.imageUpdatedDesc': 'Profile has been updated successfully',
    'profile.notifications.imageUpdateFailed': 'Failed to update profile',
    'profile.notifications.imageUpdateFailedDesc': 'An unexpected error occurred',
    
    // Site Settings
    'settings.siteSettings': 'Site Settings',
    'settings.siteSettingsDesc': 'Configure your site name and logo',
    'settings.siteName': 'Site Name',
    'settings.siteNameEn': 'Site Name (English)',
    'settings.siteNameAr': 'Site Name (Arabic)',
    'settings.saveSiteSettings': 'Save Site Settings',
    'settings.siteLogo.title': 'Site Logo',
    'settings.siteLogo.description': 'Upload and manage your site logo',
    'settings.siteLogo.recommended': 'Recommended: PNG or SVG with transparent background. Max 2MB.',
    'settings.siteLogo.alt': 'Site Logo',
    'settings.heroBackground.title': 'Background Image',
    'content.homepage.heroBackgroundImage': 'Background Image',
    'settings.heroBackground.description': 'Upload background image for the homepage main section',
    'settings.heroBackground.recommended': 'Recommended: High-quality image (1920x1080 or higher). Max 5MB. Supports PNG, JPG, WebP.',
    'settings.heroBackground.alt': 'Background Image',
    'settings.upload': 'Upload',
    'settings.uploading': 'Uploading...',
    'settings.uploadSuccess': 'Uploaded successfully',
    'settings.uploadError': 'Upload failed',
    'settings.deleteSuccess': 'Deleted successfully',
    'settings.deleteError': 'Delete failed',
    'settings.heroBackground.uploadSuccess': 'Background image uploaded successfully',
    'settings.heroBackground.uploadError': 'Failed to upload background image',
    'settings.heroBackground.deleteSuccess': 'Background image deleted successfully',
    'settings.heroBackground.deleteError': 'Failed to delete background image',
    'settings.logo.uploadSuccess': 'Logo uploaded successfully',
    'settings.logo.uploadError': 'Failed to upload logo',
    'settings.logo.deleteSuccess': 'Logo deleted successfully',
    'settings.logo.deleteError': 'Failed to delete logo',
    
    // Feature/Value/Step/FAQ CRUD messages
    'content.feature.created': 'Feature created',
    'content.feature.createdDesc': 'Feature has been created successfully',
    'content.feature.updated': 'Feature updated',
    'content.feature.updatedDesc': 'Feature has been updated successfully',
    'content.feature.deleted': 'Feature deleted',
    'content.feature.deletedDesc': 'Feature has been deleted successfully',
    'content.feature.add': 'Add Feature',
    'content.feature.edit': 'Edit Feature',
    'content.feature.order': 'Order',
    'content.feature.icon': 'Icon',
    'content.feature.titleEn': 'Title (English)',
    'content.feature.titleAr': 'Title (Arabic)',
    'content.feature.descriptionEn': 'Description (English)',
    'content.feature.descriptionAr': 'Description (Arabic)',
    'content.feature.confirmDelete': 'Are you sure you want to delete this feature?',
    'content.feature.title': 'Title',
    'content.feature.description': 'Description',
    'content.feature.status': 'Status',
    
    'content.statistic.created': 'Statistic created',
    'content.statistic.createdDesc': 'Statistic has been created successfully',
    'content.statistic.updated': 'Statistic updated',
    'content.statistic.updatedDesc': 'Statistic has been updated successfully',
    'content.statistic.deleted': 'Statistic deleted',
    'content.statistic.deletedDesc': 'Statistic has been deleted successfully',
    'content.statistic.add': 'Add Statistic',
    'content.statistic.edit': 'Edit Statistic',
    'content.statistic.order': 'Order',
    'content.statistic.number': 'Number',
    'content.statistic.labelEn': 'Label (English)',
    'content.statistic.labelAr': 'Label (Arabic)',
    'content.statistic.confirmDelete': 'Are you sure you want to delete this statistic?',
    'content.statistic.label': 'Label',
    'content.statistic.status': 'Status',
    
    'content.step.created': 'Step created',
    'content.step.createdDesc': 'Step has been created successfully',
    'content.step.updated': 'Step updated',
    'content.step.updatedDesc': 'Step has been updated successfully',
    'content.step.deleted': 'Step deleted',
    'content.step.deletedDesc': 'Step has been deleted successfully',
    'content.step.add': 'Add Step',
    'content.step.edit': 'Edit Step',
    'content.step.stepNumber': 'Step Number',
    'content.step.icon': 'Icon',
    'content.step.titleEn': 'Title (English)',
    'content.step.titleAr': 'Title (Arabic)',
    'content.step.descriptionEn': 'Description (English)',
    'content.step.descriptionAr': 'Description (Arabic)',
    'content.step.confirmDelete': 'Are you sure you want to delete this step?',
    'content.step.title': 'Title',
    'content.step.description': 'Description',
    'content.step.status': 'Status',
    
    'content.faq.created': 'FAQ created',
    'content.faq.createdDesc': 'FAQ has been created successfully',
    'content.faq.updated': 'FAQ updated',
    'content.faq.updatedDesc': 'FAQ has been updated successfully',
    'content.faq.deleted': 'FAQ deleted',
    'content.faq.deletedDesc': 'FAQ has been deleted successfully',
    'content.faq.add': 'Add FAQ',
    'content.faq.edit': 'Edit FAQ',
    'content.faq.order': 'Order',
    'content.faq.questionEn': 'Question (English)',
    'content.faq.questionAr': 'Question (Arabic)',
    'content.faq.answerEn': 'Answer (English)',
    'content.faq.answerAr': 'Answer (Arabic)',
    'content.faq.confirmDelete': 'Are you sure you want to delete this FAQ?',
    'content.faq.question': 'Question',
    'content.faq.answer': 'Answer',
    'content.faq.status': 'Status',
    
    'content.navigation.created': 'Navigation link created',
    'content.navigation.createdDesc': 'Navigation link has been created successfully',
    'content.navigation.updated': 'Navigation link updated',
    'content.navigation.updatedDesc': 'Navigation link has been updated successfully',
    'content.navigation.deleted': 'Navigation link deleted',
    'content.navigation.deletedDesc': 'Navigation link has been deleted successfully',
    'content.navigation.edit': 'Edit Navigation Link',
    'content.navigation.order': 'Order',
    'content.navigation.labelEn': 'Label (English)',
    'content.navigation.labelAr': 'Label (Arabic)',
    'content.navigation.url': 'URL',
    'content.navigation.routeName': 'Route Name',
    'content.navigation.confirmDelete': 'Are you sure you want to delete this navigation link?',
    'content.navigation.label': 'Label',
    'content.navigation.isExternal': 'External',
    'content.navigation.status': 'Status',
    
    'content.comingSoon': 'Coming Soon: This feature will be added soon',
    
    // Background
    'hero.subtitle': 'A professional system for managing tenders and projects in the contracting sector. Register now and get opportunities that match your specialty.',
    'hero.cta': 'Start Free Now',
    'hero.learnMore': 'How It Works',
    
    // Features
    'features.title': 'Why KYNZ?',
    'features.tenders': 'Tender Management',
    'features.tendersDesc': 'Browse available tenders by your specialty and submit your offers easily',
    'features.easy': 'Portfolio',
    'features.easyDesc': 'Showcase your previous projects and build your professional reputation',
    'features.secure': 'Direct Communication',
    'features.secureDesc': 'Communicate with management and track your application status in real-time',
    'features.fast': 'Security & Reliability',
    'features.fastDesc': 'Complete protection for your data and official documents',
    
    // Stats
    'stats.tenders': 'Published Tenders',
    'stats.clients': 'Registered Clients',
    'stats.projects': 'Completed Projects',
    'stats.specialties': 'Specialties',
    
    // Latest Tenders
    'latestTenders.viewAll': 'View All Projects',
    
    // CTA
    'cta.title': 'Join Us Today',
    'cta.subtitle': 'Register now and get notifications of the latest tenders in your specialty',
    'cta.button': 'Register for Free',
    
    // Dashboard CTA (for authenticated users)
    'cta.dashboard.title.admin': 'Welcome to Your Data Panel',
    'cta.dashboard.title.client': 'Welcome to Your Account',
    'cta.dashboard.subtitle.admin': 'Manage the platform and control all features',
    'cta.dashboard.subtitle.client': 'Manage your projects and track tenders',
    'cta.dashboard.button': 'Go to Data Panel',
    
    // Dashboard
    'dashboard.welcome': 'Welcome',
    'dashboard.welcomeMessage': 'Here is your activity summary on the platform',
    'dashboard.totalOffers': 'Total Offers',
    'dashboard.pendingOffers': 'Pending Offers',
    'dashboard.acceptedOffers': 'Accepted Offers',
    'dashboard.rejectedOffers': 'Rejected Offers',
    'dashboard.totalProjects': 'Total Projects',
    'dashboard.approvedProjects': 'Approved Projects',
    'dashboard.activeTenders': 'Active Tenders',
    'dashboard.myApplications': 'My Applications',
    'dashboard.pending': 'Pending Review',
    'dashboard.accepted': 'Accepted',
    'dashboard.latestTenders': 'Latest Tenders',
    'dashboard.recentTenders': 'Recent Tenders',
    'dashboard.noRecentTenders': 'No Recent Tenders',
    'dashboard.notifications': 'Notifications',
    'dashboard.viewAll': 'View All',
    'dashboard.browseTenders': 'Browse Tenders',
    'dashboard.findNewTenders': 'Find New Opportunities',
    'dashboard.myPortfolio': 'My Portfolio',
    'dashboard.managePortfolio': 'Manage Portfolio',
    'dashboard.myTenders': 'My Tenders',
    
    // Tender Details
    'tenders.tenderDetails': 'Tender Details',
    'tenders.basicInfo': 'Basic Information',
    'tenders.budget': 'Budget',
    'tenders.minBudget': 'Minimum Budget',
    'tenders.maxBudget': 'Maximum Budget',
    'tenders.deadlineNote': 'Deadline for offer submission',
    'tenders.tenderNotFound': 'Tender not found',
    'common.back': 'Back',
    
    // Status
    'status.pending': 'Pending',
    'status.approved': 'Approved',
    'status.rejected': 'Rejected',
    'status.suspended': 'Suspended',
    
    // Settings
    'settings.title': 'Settings',
    'settings.language': 'Language',
    'settings.notifications': 'Notifications',
    'settings.adminSettings': 'Admin Settings',
    'settings.fileSizeSettings': 'File Size Settings',
    'settings.maxFileSize': 'Maximum File Size',
    'settings.maxFileSizeDesc': 'Maximum size of files users can upload (in MB)',
    'settings.enterCustomSize': 'Enter custom size in MB',
    'settings.addCustomFileType': 'Add Custom File Type',
    'settings.enterFileExtension': 'Enter file extension (without dot)',
    'settings.selectedFileTypes': 'Selected File Types',
    'settings.emailHost': 'Email Host',
    'settings.emailPort': 'Email Port',
    'settings.emailUsername': 'Username',
    'settings.emailPassword': 'Password',
    'settings.emailFrom': 'From Email',
    'settings.emailFromName': 'From Name',
    'settings.emailEncryption': 'Encryption',
    'settings.save': 'Save',
    'settings.saved': 'Saved',
    'settings.savedDesc': 'Settings saved successfully',
    'settings.mb': 'MB',
    'settings.selectFileTypes': 'Select file types',
    
    // Auth
    'auth.confirmPassword': 'Confirm Password',
    'auth.accountCreated': 'Account Created',
    'auth.loggedIn': 'Logged In',
    'auth.accountReview': 'Your account will be reviewed and we will contact you soon',
    'auth.welcomeBack': 'Welcome to KYNZ Platform',
    'auth.createAccount': 'Create New Account',
    'auth.login': 'Login',
    'auth.joinPlatform': 'Join the leading tender platform',
    'auth.welcomeBackMessage': 'Welcome back to KYNZ',
    'auth.fullName': 'Full Name',
    'auth.phone': 'Phone Number',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.forgotPassword': 'Forgot Password?',
    'auth.haveAccount': 'Already have an account?',
    'auth.noAccount': "Don't have an account?",
    'auth.registerNow': 'Register Now',
    'auth.backToHome': 'Back to Home',
    'auth.passwordChanged': 'Password Changed',
    'auth.passwordChangedDesc': 'Your password has been updated successfully',
    'auth.changePassword': 'Change Password',
    'auth.currentPassword': 'Current Password',
    'auth.newPassword': 'New Password',
    'auth.updatePassword': 'Update Password',
    'auth.loginFailed': 'Login Failed',
    'auth.registerFailed': 'Registration Failed',
    'auth.emailPlaceholder': 'Enter your email',
    'auth.passwordPlaceholder': 'Enter your password',
    'auth.secureConnection': 'Secure Connection',
    'auth.dataProtection': 'Data Protection',
    'auth.support247': '24/7 Support',
    'auth.basicInfo': 'Basic Information',
    'auth.basicInfoDesc': 'Enter your basic information to create an account',
    'auth.companyInfo': 'Company Information',
    'auth.companyInfoDesc': 'Enter your company information and specialties',
    'auth.documents': 'Required Documents',
    'auth.requiredDocuments': 'Required Documents',
    'auth.documentsDesc': 'Upload the required official documents',
    'auth.step': 'Step',
    'auth.of': 'of',
    'auth.previous': 'Previous',
    'auth.next': 'Next',
    'auth.submit': 'Submit',
    'auth.registering': 'Registering...',
    'auth.platformFeatures': 'Platform Features',
    'auth.securePlatform': 'Secure Platform',
    'auth.fastEasy': 'Fast & Easy',
    'auth.expertSupport': 'Expert Support',
    'auth.registrationSuccess': 'Registration Successful',
    'auth.registrationSuccessDesc': 'Your account will be reviewed and we will contact you soon',
    'auth.backToLogin': 'Back to Login',
    'auth.cancel': 'Cancel',
    'auth.completed': 'Completed',
    'auth.dragDropFile': 'Drag and drop file here',
    'auth.supportedFormats': 'Supported formats',
    'auth.maxFileSize': 'Maximum file size',
    'auth.securityNotice': 'Security Notice',
    'auth.securityNoticeDesc': 'Your data is secure and protected',
    'auth.completeRegistration': 'Complete Registration',
    'auth.secure100': '100% Secure',
    'auth.easyToUse': 'Easy to Use',
    'auth.realOpportunities': 'Real Opportunities',
    
    // Validation
    'validation.required': 'This field is required',
    'validation.email': 'Invalid email address',
    'validation.passwordMin': 'Password must be at least 8 characters',
    'validation.passwordMatch': 'Passwords do not match',
    'validation.specializationRequired': 'At least one specialty must be selected',
    'validation.documentRequired': 'This document is required',
    'validation.fileSize': 'File size too large',
    'validation.fileType': 'File type not supported',
    
    // Profile
    'profile.licenseNumber': 'Commercial Register Number',
    'profile.taxId': 'Tax ID',
    'profile.profileImage': 'Profile Image',
    'profile.profileImageDesc': 'Personal photo or company logo (optional)',
    'profile.selectSpecializations': 'Select Specializations',
    'profile.specializations': 'Specializations',
    'profile.specializationsDesc': 'Select the specialties that match your business',
    'profile.uploadDocuments': 'Upload Documents',
    'profile.uploadDocumentsDesc': 'Upload the required official documents',
    'profile.commercialRegister': 'Commercial Register',
    'profile.taxCertificate': 'Tax Certificate',
    'profile.ibanCertificate': 'IBAN Certificate',
    'profile.zakatIncomeCertificate': 'Zakat and Income Certificate',
    'profile.socialInsuranceCertificate': 'Content Certificate',
    'profile.companyProfile': 'Company Profile',
    
    // Portfolio
    'portfolio.clientPortfolio': 'My Portfolio',
    'portfolio.addProject': 'Add your projects to showcase them to clients',
    'portfolio.totalProjects': 'Total Projects',
    'portfolio.status': 'Status',
    'portfolio.addNew': 'Add Work Experience',
    'portfolio.confirmDelete': 'Are you sure you want to delete this project?',
    'portfolio.adminNotAvailable': 'Portfolio is only available for clients',
    'portfolio.adminMessage': 'Administrators do not have a portfolio section. Please navigate to the admin dashboard.',
    'portfolio.goToAdmin': 'Go to Admin Dashboard',
    'portfolio.noProjects': 'No Projects',
    'portfolio.noProjectsDesc': 'Start by adding your first project to showcase it to clients',
    'portfolio.addFirstProject': 'Add First Work Experience',
    'portfolio.editProjectTitle': 'Edit Project',
    'portfolio.addNewTitle': 'Add Work Experience',
    'portfolio.editProjectDesc': 'Edit project details',
    'portfolio.addProjectDesc': 'Add new work experience to your portfolio',
    'portfolio.projectDetails': 'Project Details',
    'portfolio.newProjectDetails': 'New Work Experience Details',
    'portfolio.projectInfo': 'Project Information',
    'portfolio.projectNameAr': 'Project Name (Arabic)',
    'portfolio.projectNameEn': 'Project Name (English)',
    'portfolio.projectDescriptionAr': 'Project Description (Arabic)',
    'portfolio.projectDescriptionEn': 'Project Description (English)',
    'portfolio.projectLocationAr': 'Location (Arabic)',
    'portfolio.projectLocationEn': 'Location (English)',
    'portfolio.year': 'Year',
    'portfolio.month': 'Month',
    'portfolio.day': 'Day',
    'portfolio.completedDate': 'Completion Date',
    'portfolio.projectImages': 'Project Images',
    'portfolio.dragDropImages': 'Drag and drop images here or click to select',
    'portfolio.supportedImageFormats': 'Supported formats: JPG, PNG, WEBP',
    'portfolio.maxImageSize': 'Maximum image size: 5MB',
    'portfolio.projectAdded': 'Project added successfully',
    'portfolio.projectUpdated': 'Project updated successfully',
    'portfolio.addError': 'Failed to add project',
    'portfolio.updateError': 'Failed to update project',
    'portfolio.viewProject': 'View Project',
    'portfolio.editProject': 'Edit Project',
    'portfolio.deleteProject': 'Delete Project',
    'portfolio.imageError': 'Please select image files only',
    'portfolio.requiredField': 'This field is required',
    'portfolio.projectName': 'Project Name',
    'portfolio.description': 'Project Description',
    'portfolio.locationAr': 'Location',
    'portfolio.locationEn': 'Location',
    'portfolio.selectDate': 'Select Date',
    'portfolio.uploadImages': 'Upload Images',
    'portfolio.saveProject': 'Save Project',
    'portfolio.cancel': 'Cancel',
    'portfolio.loading': 'Loading...',
    'portfolio.noImages': 'No Images',
    'portfolio.addImages': 'Add Images',
    'portfolio.removeImage': 'Remove Image',
    'portfolio.projectTitle': 'Project Title',
    'portfolio.projectContent': 'Project Content',
    'portfolio.viewingProject': 'Viewing Project',
    'portfolio.actions': 'Actions',
    'portfolio.backToPortfolio': 'Back to Portfolio',
    'portfolio.projectId': 'Project ID',
    'portfolio.totalImages': 'Total Images',
    'portfolio.images': 'Images',
    'portfolio.location': 'Location',
    'portfolio.imageFormats': 'Image Formats',
    'portfolio.existingImages': 'Existing Images',
    'portfolio.totalImagesLabel': 'Total Images:',
    'portfolio.joinUs': 'Join Us',
    'portfolio.contactUs': 'Contact Us',
    'portfolio.ourProjects': 'Our Projects',
    'portfolio.projectsDescription': 'Browse our latest completed projects',
    'portfolio.requestProject': 'Request Project',
    'portfolio.readyToStart': 'Ready to Start?',
    'portfolio.readyDescription': 'Contact us today to discuss your next project',
    'portfolio.getStarted': 'Get Started',
    'portfolio.learnMore': 'Learn More',
    'portfolio.distinguishedProjects': 'Distinguished Projects',
    'portfolio.highQuality': 'High Quality',
    'portfolio.comprehensiveGuarantees': 'Comprehensive Guarantees',
    
    // Documents
    'documents.commercialRegister': 'Commercial Register',
    'documents.taxCertificate': 'Tax Certificate',
    'documents.ibanCertificate': 'IBAN Certificate',
    'documents.zakatCertificate': 'Zakat and Income Certificate',
    'documents.socialInsurance': 'Content Certificate',
    'documents.companyProfile': 'Company Profile',
    'documents.commercialRegisterDesc': 'Official commercial register of the company',
    'documents.taxCertificateDesc': 'Tax certificate issued by relevant authorities',
    'documents.ibanCertificateDesc': 'International Bank Account Number (IBAN) certificate',
    'documents.zakatCertificateDesc': 'Zakat and Income certificate',
    'documents.socialInsuranceDesc': 'Content certificate for digital content (optional)',
    'documents.companyProfileDesc': 'Comprehensive company profile including establishment details',
    
    // Additional Profile Keys
    'profile.overview': 'Overview',
    'profile.editProfile': 'Edit Profile',
    'profile.documents': 'Documents',
    'profile.changePassword': 'Change Password',
    'profile.companyInfo': 'Company Information',
    'profile.companyName': 'Company Name',
    'profile.email': 'Email',
    'profile.phone': 'Phone Number',
    'profile.address': 'Address',
    'profile.description': 'Description',
    'profile.profilePicture': 'Profile Picture',
    'profile.cannotDeleteApproved': 'Cannot delete approved project',
    'profile.approvedByAdmin': 'Approved by administrator',
    'profile.approved': 'Approved',
    'profile.emailReadOnly': 'Email is read-only',
    'profile.noSpecializations': 'No specializations',
    'profile.personalInfo': 'My Personal Information',
    
    // Password Change
    'auth.passwordMismatch': 'Password Error',
    'auth.passwordsDoNotMatch': 'New password and password confirmation do not match',
    'auth.passwordTooShort': 'Password Too Short',
    'auth.passwordMinLength': 'Password must be at least 8 characters',
    
    // Common
    'common.approved': 'Approved',
    'common.rejected': 'Rejected',
    'common.pending': 'Pending',
    'common.error': 'Error',
    'common.success': 'Success',
    'common.retry': 'Retry',
    'common.download': 'Download',
    'common.uploading': 'Uploading...',
    'common.fileSelected': 'file selected',
    'common.filesSelected': 'files selected',
    'readOnly.disabled': 'This action is disabled',
    
    // Dashboard
    'dashboard.summary': "Here's your activity summary for today",
    'dashboard.deadline': 'Deadline',
    'dashboard.tenderNew': 'New',
    'dashboard.tenderUrgent': 'Urgent',
    'dashboard.notificationAccepted': 'Your steel supply tender application was accepted',
    'dashboard.notificationNewTender': 'New tender matching your specialty',
    'dashboard.notificationReminder': 'Reminder: Tender closes tomorrow',
    'dashboard.timeAgo1h': '1 hour ago',
    'dashboard.timeAgo3h': '3 hours ago',
    'dashboard.timeAgo5h': '5 hours ago',
    'dashboard.tenderSteel': 'Steel Supply Tender',
    'dashboard.tenderPaint': 'Administrative Building Paint Tender',
    'dashboard.tenderCement': 'Resistant Cement Supply',
    
    // Profile
    'profile.title': 'Profile',
    'profile.subtitle': 'Manage your company data and documents',
    'profile.basicInfo': 'Basic Information',
    'profile.specialization': 'Specialization',
    'profile.selectSpecialization': 'Select Specialization',
    'profile.companyDescription': 'Company Description',
    'profile.saveChanges': 'Save Changes',
    'profile.changesSaved': 'Changes Saved',
    'profile.changesSavedDesc': 'Profile data updated successfully',
    'profile.zakatCertificate': 'Zakat and Income Certificate',
    'profile.isoCertificate': 'ISO Quality Certificate',
    'profile.verified': 'Verified',
    'profile.pending': 'Under Review',
    'profile.uploadDocument': 'Upload New Document',
    'profile.specialization.steel': 'Steel',
    'profile.specialization.cement': 'Cement',
    'profile.specialization.paint': 'Paint',
    'profile.specialization.construction': 'General Construction',
    'profile.specialization.infrastructure': 'Infrastructure',
    'profile.currentPassword': 'Current Password',
    'profile.enterCurrentPassword': 'Enter your current password',
    'profile.newPassword': 'New Password',
    'profile.enterNewPassword': 'Enter your new password',
    'profile.confirmNewPassword': 'Confirm New Password',
    'profile.enterConfirmNewPassword': 'Confirm your new password',
    'profile.noDocuments': 'No documents',
    'profile.optional': 'Optional',
    'profile.allowedFiles': 'PDF, DOC, DOCX, JPG, PNG',
    'profile.searchSpecializations': 'Search specializations...',
    'profile.noSpecializationsFound': 'No specializations found',
    'tenders.browse': 'Browse the latest tenders and register to get full details',
    'tenders.search': 'Search tenders...',
    'tenders.specialization': 'Specialization',
    'tenders.allSpecializations': 'All Specializations',
    'tenders.region': 'Region',
    'tenders.allRegions': 'All Regions',
    'tenders.open': 'Open',
    'tenders.closed': 'Closed',
    'tenders.closingSoon': 'Closing Soon',
    'tenders.new': 'New',
    'tenders.deadline': 'Deadline',
    'tenders.finalDeadline': 'Final Deadline',
    'tenders.submitOffer': 'Submit Tender Application',
    'tenders.documents': 'Documents',
    'tenders.viewDetails': 'View Details',
    'tenders.registerMessage': 'Register your account to get all details and submit offers',
    'tenders.registerNow': 'Register Now',
    'tenders.riyadh': 'Riyadh',
    'tenders.jeddah': 'Jeddah',
    'tenders.dammam': 'Dammam',
    'tenders.makkah': 'Makkah',
    'tenders.steel': 'Steel',
    'tenders.cement': 'Cement',
    'tenders.paint': 'Paint',
    'tenders.sar': 'SAR',
    
    // Applications
    'applications.title': 'My Applications',
    'applications.subtitle': 'Track your submitted applications status',
    'applications.all': 'All',
    'applications.pending': 'Pending',
    'applications.accepted': 'Accepted',
    'applications.rejected': 'Rejected',
    'applications.noApplications': 'No applications',
    'applications.submittedDate': 'Submitted Date',
    'applications.submittedPrice': 'Submitted Price',
    'applications.rejectedBadge': 'Rejected',
    'applications.acceptedBadge': 'Accepted',
    'applications.attachments': 'Attachments',
    
    // Portfolio
    'portfolio.title': 'Portfolio',
    'portfolio.subtitle': 'Samples of projects completed through our platform',
    'portfolio.clientName': 'Client Name',
    'portfolio.descriptionAr': 'Project Description (Arabic)',
    'portfolio.descriptionEn': 'Project Description (English)',
    'portfolio.addProjectBtn': 'Add Work Experience',
    'portfolio.updateProjectBtn': 'Update Project',
    'portfolio.viewDetails': 'View Details',
    'portfolio.edit': 'Edit',
    'portfolio.delete': 'Delete',
    'portfolio.notApproved': 'Account Pending Approval',
    'portfolio.notApprovedDesc': 'Your account is pending approval. Please wait for admin approval before adding or editing projects.',
    'portfolio.approvalRequired': 'Approval Required',
    'portfolio.approvalRequiredDesc': 'Your account must be approved to manage projects.',
    'portfolio.yearHint': 'Enter year only (e.g., 2023)',
    'portfolio.projectNotFound': 'Project not found',
    'portfolio.projectAddedDesc': 'Project added successfully',
    'portfolio.projectUpdatedDesc': 'Project updated successfully',
    'portfolio.projectDeleted': 'Project Deleted',
    'portfolio.projectDeletedDesc': 'Project deleted successfully',
    'portfolio.addFailed': 'Add Failed',
    'portfolio.updateFailed': 'Update Failed',

    
    'portfolio.deleteFailed': 'Delete Failed',
    'portfolio.newProjectDetailsDesc': 'Add new project details',
    'profile.status': 'Status',
    'profile.createdAt': 'Created At',
    'profile.updatedAt': 'Updated At',
    
    // Admin Clients
    'admin.clients.title': 'Manage Clients',
    'admin.clients.subtitle': 'View and manage all clients',
    'admin.clients.adminOnly': 'This page is only available for administrators',
    'admin.clients.searchPlaceholder': 'Search for a client...',
    'admin.clients.filterStatus': 'Filter by Status',
    'admin.clients.allStatuses': 'All Statuses',
    'admin.clients.suspended': 'Suspended',
    'admin.clients.viewDetails': 'View Details',
    'admin.clients.approve': 'Approve',
    'admin.clients.reject': 'Reject',
    'admin.clients.suspend': 'Suspend',
    'admin.clients.delete': 'Delete',
    'admin.clients.clientDetails': 'Client Details',
    'admin.clients.clientDetailsDesc': 'Detailed information about the client',
    'admin.clients.name': 'Name',
    'admin.clients.email': 'Email',
    'admin.clients.companyName': 'Company Name',
    'admin.clients.phone': 'Phone',
    'admin.clients.address': 'Address',
    'admin.clients.registrationDate': 'Registration Date',
    'admin.clients.clientNotFound': 'Client not found',
    'admin.clients.noClients': 'No clients',
    'admin.clients.confirmDelete': 'Are you sure you want to delete this client?',
    'admin.clients.statusUpdated': 'Status Updated',
    'admin.clients.approved': 'Approved',
    'admin.clients.rejected': 'Rejected',
    'admin.clients.updateFailed': 'Update Failed',
    'admin.clients.clientDeleted': 'Client Deleted',
    'admin.clients.clientDeletedDesc': 'Client deleted successfully',
    'admin.clients.deleteFailed': 'Delete Failed',
    'admin.clients.clientCreated': 'Client Created',
    'admin.clients.clientCreatedDesc': 'Client created successfully',
    'admin.clients.createFailed': 'Create Failed',
    'admin.clients.userInfo': 'User Information',
    'admin.clients.clientInfo': 'Client Information',
    'admin.clients.userType': 'User Type',
    'admin.clients.userCreatedAt': 'User Created At',
    'admin.clients.clientCreatedAt': 'Client Created At',
    'admin.clients.licenseNumber': 'Commercial Register Number',
    'admin.clients.taxId': 'Tax ID',
    'admin.clients.specializations': 'Specializations',
    'admin.clients.projects': 'Projects',
    'admin.clients.reactivate': 'Reactivate',
    'admin.clients.createClient': 'Create New Client',
    'admin.clients.createClientDesc': 'Add a new client to the system',
    'admin.clients.create': 'Create Client',
    'admin.clients.createDescription': 'Create a new client account in the system',
    'admin.clients.edit': 'Edit',
    'admin.clients.status': 'Status',
    'admin.clients.actions': 'Actions',
    'admin.clients.overview': 'Overview',
    'admin.clients.documents': 'Documents',
    'admin.clients.noDocuments': 'No documents',
    'admin.clients.noSpecializations': 'No specializations',
    'admin.clients.noProjects': 'No projects',
    'admin.clients.client': 'Client',
    'admin.clients.profileComplete': 'Profile Complete',
    'admin.clients.rejectReason': 'Rejection Reason',
    'admin.clients.rejectReasonDesc': 'Enter the reason for rejecting the client',
    'admin.clients.rejectReasonPlaceholder': 'Write the rejection reason here...',
    
    // Tenders
    'tenders.noTenders': 'No tenders available',
    'tenders.manage': 'Manage Tenders',
    'tenders.manageDesc': 'Create and manage tenders',
    'tenders.create': 'Create Tender',
    'tenders.edit': 'Edit',
    'tenders.delete': 'Delete',
    'tenders.confirmDelete': 'Are you sure you want to delete this tender?',
    'tenders.createTender': 'Create New Tender',
    'tenders.editTender': 'Edit Tender',
    'tenders.createTenderDesc': 'Create a new tender',
    'tenders.editTenderDesc': 'Update tender information',
    'tenders.update': 'Update',
    'tenders.titleEn': 'Title (English)',
    'tenders.titleAr': 'Title (Arabic)',
    'tenders.titleEnPlaceholder': 'Enter title in English',
    'tenders.titleArPlaceholder': 'أدخل العنوان بالعربية',
    'tenders.titleRequired': 'Title must be entered in both languages',
    'tenders.descriptionEn': 'Description (English)',
    'tenders.descriptionAr': 'Description (Arabic)',
    'tenders.descriptionEnPlaceholder': 'Enter description in English',
    'tenders.descriptionArPlaceholder': 'أدخل الوصف بالعربية',
    'tenders.requirementsEn': 'Requirements (English)',
    'tenders.requirementsAr': 'Requirements (Arabic)',
    'tenders.requirementsEnPlaceholder': 'Enter requirements in English',
    'tenders.requirementsArPlaceholder': 'أدخل المتطلبات بالعربية',
    'tenders.requirements': 'Requirements',
    'tenders.budgetMin': 'Minimum Budget',
    'tenders.budgetMax': 'Maximum Budget',
    'tenders.budgetError': 'Minimum must be less than maximum',
    'tenders.selectDeadline': 'Select Date',
    'tenders.status': 'Status',
    'tenders.pending': 'Pending',
    'tenders.visibilityType': 'Visibility Type',
    'tenders.allClients': 'All Clients',
    'tenders.bySpecializations': 'By Specializations',
    'tenders.specificClients': 'Specific Clients',
    'tenders.selectSpecializations': 'Select Specializations',
    'tenders.selectClients': 'Select Clients',
    'tenders.specializationsRequired': 'At least one specialization must be selected',
    'tenders.clientsRequired': 'At least one client must be selected',
    'tenders.allStatuses': 'All Statuses',
    'tenders.filterStatus': 'Filter by Status',
    'tenders.created': 'Tender Created',
    'tenders.updated': 'Tender Updated',
    'tenders.deleted': 'Tender Deleted',
    'tenders.createdDesc': 'Tender created successfully',
    'tenders.updatedDesc': 'Tender updated successfully',
    'tenders.deletedDesc': 'Tender deleted successfully',
    'tenders.createFailed': 'Failed to create tender',
    'tenders.updateFailed': 'Failed to update tender',
    'tenders.deleteFailed': 'Failed to delete tender',
    
    // Admin Tenders
    'admin.tenders.title': 'Tender Management',
    'admin.tenders.subtitle': 'View and manage all tenders in the system',
    'admin.tenders.createTender': 'Create Tender',
    'admin.tenders.searchPlaceholder': 'Search for tenders...',
    'admin.tenders.allStatuses': 'All Statuses',
    'admin.tenders.filterStatus': 'Filter by Status',
    'admin.tenders.columnTitle': 'Title',
    'admin.tenders.budget': 'Budget',
    'admin.tenders.deadline': 'Deadline',
    'admin.tenders.status': 'Status',
    'admin.tenders.created': 'Created',
    'admin.tenders.actions': 'Actions',
    'admin.tenders.view': 'View',
    'admin.tenders.edit': 'Edit',
    'admin.tenders.delete': 'Delete',
    'admin.tenders.activate': 'Activate',
    'admin.tenders.close': 'Close',
    'admin.tenders.adminOnly': 'This page is only available for administrators',
    'admin.tenders.noTenders': 'No tenders available',
    'admin.tenders.tenderDetails': 'Tender Details',
    'admin.tenders.tenderDetailsDesc': 'Detailed information about the tender',
    'admin.tenders.basicInfo': 'Basic Information',
    'admin.tenders.documents': 'Documents',
    'admin.tenders.uploadInstructions': 'Drag and drop files here or click to select them',
    'admin.tenders.noDocuments': 'No documents uploaded',
    'admin.tenders.uploadDocuments': 'Upload Documents',
    'admin.tenders.allowedFileTypes': 'PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG (Max: 10MB per file)',
    'admin.tenders.uploadedFiles': 'Uploaded Files',
    'admin.tenders.description': 'Description',
    'admin.tenders.financialInfo': 'Financial Information',
    'admin.tenders.minBudget': 'Minimum Budget',
    'admin.tenders.maxBudget': 'Maximum Budget',
    'admin.tenders.statusAndDates': 'Status & Dates',
    'admin.tenders.createdAt': 'Created At',
    'admin.tenders.visibility': 'Visibility Type',
    'admin.tenders.allUsers': 'All Users',
    'admin.tenders.specializationsOnly': 'Specializations Only',
    'admin.tenders.specificClients': 'Specific Clients',
    'admin.tenders.specializations': 'Specializations',
    'admin.tenders.editTenderDesc': 'Edit tender information',
    'admin.tenders.offers': 'Offers',
    'admin.tenders.details': 'Details',
    'admin.tenders.createTenderDesc': 'Add a new tender',
    'admin.tenders.titleEn': 'Title (English)',
    'admin.tenders.titleAr': 'Title (Arabic)',
    'admin.tenders.descriptionEn': 'Description (English)',
    'admin.tenders.descriptionAr': 'Description (Arabic)',
    'admin.tenders.requirementsEn': 'Requirements (English)',
    'admin.tenders.requirementsAr': 'Requirements (Arabic)',
    'admin.tenders.titleEnPlaceholder': 'Enter tender title in English',
    'admin.tenders.titleArPlaceholder': 'Enter tender title in Arabic',
    'admin.tenders.descriptionEnPlaceholder': 'Enter tender description in English',
    'admin.tenders.descriptionArPlaceholder': 'Enter tender description in Arabic',
    'admin.tenders.requirementsEnPlaceholder': 'Enter tender requirements in English',
    'admin.tenders.requirementsArPlaceholder': 'Enter tender requirements in Arabic',
    'admin.tenders.minBudgetLabel': 'Minimum Budget',
    'admin.tenders.maxBudgetLabel': 'Maximum Budget',
    'admin.tenders.deadlinePlaceholder': 'mm/dd/yyyy',
    'admin.tenders.statusLabel': 'Status',
    'admin.tenders.visibilityLabel': 'Visibility Type',
    'admin.tenders.dragDrop': 'Drag and drop files here or click to select them',
    'admin.tenders.cancel': 'Cancel',
    'admin.tenders.update': 'Update',

    // Admin Users Management (English)
    'admin.users.title': 'Admin Management',
    'admin.users.subtitle': 'View and manage administrators in the system',
    'admin.users.create': 'Add Admin',
    'admin.users.edit': 'Edit Admin',
    'admin.users.delete': 'Delete Admin',
    'admin.users.name': 'Name',
    'admin.users.email': 'Email',
    'admin.users.position': 'Position',
    'admin.users.isSuperAdmin': 'System Admin',
    'admin.users.status': 'Status',
    'admin.users.createdAt': 'Created At',
    'admin.users.actions': 'Actions',
    'admin.users.noUsers': 'No administrators found',
    'admin.users.searchPlaceholder': 'Search for admins...',
    'admin.users.superAdminOnly': 'This feature is only available for system administrators',
    'admin.users.resetPassword': 'Reset Password',
    'admin.users.password': 'Password',
    'admin.users.confirmPassword': 'Confirm Password',
    'admin.users.passwordPlaceholder': 'Enter password',
    'admin.users.positionPlaceholder': 'Enter position (optional)',
    'admin.users.createSuccess': 'Administrator created successfully',
    'admin.users.updateSuccess': 'Administrator updated successfully',
    'admin.users.deleteSuccess': 'Administrator deleted successfully',
    'admin.users.passwordResetSuccess': 'Password reset successfully',
    'admin.users.cannotDeleteSelf': 'You cannot delete your own account',
    'admin.users.cannotDeleteLastSuperAdmin': 'Cannot delete the last system administrator',
    'admin.users.cannotDemoteLastSuperAdmin': 'Cannot demote the last system administrator',

    // Dashboard
    'dashboard.on': 'on',
    'dashboard.proposedAmount': 'Proposed Amount',
    'dashboard.submittedBy': 'Submitted By',
    'dashboard.viewOffer': 'View Offer',
    'dashboard.manageClients': 'Manage Clients',
    'dashboard.viewManageClients': 'View Manage Clients',
    'dashboard.manageTenders': 'Manage Tenders',
    'dashboard.viewManageTenders': 'View Manage Tenders',
    'dashboard.manageContent': 'Manage Content',
    'dashboard.viewManageContent': 'View Manage Content',
    
    // Specializations
    'specializations.confirmDelete': 'Are you sure you want to delete this specialization?',
    
    // Common
    'offers.title': 'Offers',
    'offers.subtitle': 'Manage submitted offers',
    'offers.myOffers': 'My Offers',
    'offers.allOffers': 'All Offers',
    'offers.submitOffer': 'Submit Tender Application',
    'offers.submitNew': 'Submit Tender Application',
    'offers.fileTooLarge': 'File is too large',
    'offers.maxSize': 'Max size 10MB',
    'offers.invalidFileType': 'File type not supported',
    'offers.amountOutOfRange': 'Amount is out of allowed range',
    'offers.documentsRequired': 'At least one financial or technical document must be uploaded',
    'offers.edit': 'Edit',
    'offers.delete': 'Delete',
    'offers.accept': 'Accept',
    'offers.reject': 'Reject',
    'offers.viewDetails': 'View Details',
    'offers.tender': 'Tender',
    'offers.client': 'Client',
    'offers.proposedAmount': 'Proposed Amount',
    'offers.notes': 'Notes',
    'offers.status': 'Status',
    'offers.pending': 'Pending',
    'offers.statusAccepted': 'Accepted',
    'offers.statusRejected': 'Rejected',
    'offers.submittedDate': 'Submitted Date',
    'offers.noOffers': 'No offers',
    'offers.confirmDelete': 'Are you sure you want to delete this offer?',
    'offers.confirmAccept': 'Are you sure you want to accept this offer?',
    'offers.confirmReject': 'Are you sure you want to reject this offer?',
    'offers.submitted': 'Offer Submitted',
    'offers.submittedDesc': 'Offer submitted successfully',
    'offers.updated': 'Offer Updated',
    'offers.updatedDesc': 'Offer updated successfully',
    'offers.deleted': 'Offer Deleted',
    'offers.deletedDesc': 'Offer deleted successfully',
    'offers.acceptedTitle': 'Offer Accepted',
    'offers.acceptedDesc': 'Offer accepted successfully',
    'offers.rejectedTitle': 'Offer Rejected',
    'offers.rejectedDesc': 'Offer rejected successfully',
    'offers.submitFailed': 'Failed to submit offer',
    'offers.submitFailedDesc': 'An error occurred while submitting the offer',
    'offers.updateFailed': 'Failed to update offer',
    'offers.updateFailedDesc': 'An error occurred while updating the offer',
    'offers.deleteFailed': 'Failed to delete offer',
    'offers.deleteFailedDesc': 'An error occurred while deleting the offer',
    'offers.acceptFailed': 'Failed to accept offer',
    'offers.rejectFailed': 'Failed to reject offer',
    'offers.tenderId': 'Tender',
    'offers.selectTender': 'Select Tender',
    'offers.amount': 'Amount',
    'offers.amountPlaceholder': 'Enter proposed amount',
    'offers.notesPlaceholder': 'Enter your notes (optional)',
    'offers.filterStatus': 'Filter by Status',
    'offers.allStatuses': 'All Statuses',
    'offers.filterTender': 'Filter by Tender',
    'offers.allTenders': 'All Tenders',
    'offers.enterAmount': 'Enter amount',
    'offers.budgetRange': 'Budget Range',
    'offers.enterNotes': 'Enter notes',
    'offers.attachments': 'Attachments',
    'offers.selectFiles': 'Select files',
    'offers.allowedFiles': 'Allowed files: PDF, DOC, DOCX, JPG, PNG (max 10MB)',
    'offers.offerDetails': 'Offer Details',
    'offers.financialDocuments': 'Financial Offer',
    'offers.financialDocumentsDesc': 'Upload financial offer files (prices, costs, etc.)',
    'offers.technicalDocuments': 'Technical Offer',
    'offers.technicalDocumentsDesc': 'Upload technical offer files (methodology, expertise, etc.)',
    'offers.documentType': 'Document Type',
    'offers.documentTypeFinancial': 'Financial',
    'offers.documentTypeTechnical': 'Technical',
    
    // Dashboard
    'dashboard.noTenders': 'No tenders',
    'dashboard.noNotifications': 'No notifications',
    'dashboard.totalTenders': 'Total Tenders',
    'dashboard.totalClients': 'Total Clients',
    'dashboard.pendingClients': 'Pending Clients',
    'dashboard.trackApplications': 'Track Applications',
    
    // Profile
    // Notifications
    'notifications.title': 'Notifications',
    'notifications.subtitle': 'Follow the latest updates and alerts',
    'notifications.markAllRead': 'Mark All as Read',
    'notifications.offerAccepted': 'Your Offer Accepted',
    'notifications.offerAcceptedDesc': 'Your offer on metal structures tender was accepted',
    'notifications.newTender': 'New Tender',
    'notifications.newTenderDesc': 'New tender matching your specialty: Steel Supply',
    'notifications.deadlineReminder': 'Deadline Reminder',
    'notifications.deadlineReminderDesc': 'The deadline for submitting offers on cement tender ends tomorrow',
    'notifications.requestRejected': 'Request Rejected',
    'notifications.requestRejectedDesc': 'Unfortunately, your request on infrastructure tender was rejected',
    'notifications.timeAgo2h': '2 hours ago',
    'notifications.timeAgo5h': '5 hours ago',
    'notifications.timeAgo1d': '1 day ago',
    'notifications.timeAgo3d': '3 days ago',
    
    // About
    'about.title': 'About KYNZ Contracting',
    'about.subtitle': 'A leading platform for managing tenders and connecting contractors with suitable opportunities',
    'about.vision': 'Our Vision',
    'about.visionDesc': 'To be the first and most reliable platform in the contracting sector for connecting project owners with qualified contractors and suppliers in the Arab region.',
    'about.mission': 'Our Mission',
    'about.missionDesc': 'To facilitate the tendering process and provide a transparent and fair environment that enables everyone to access suitable opportunities and achieve mutual success.',
    'about.values': 'Our Values',
    'about.quality': 'Quality',
    'about.qualityDesc': 'We strive to provide the best possible service to our clients',
    'about.transparency': 'Transparency',
    'about.transparencyDesc': 'We work with complete clarity and integrity in all our dealings',
    'about.innovation': 'Innovation',
    'about.innovationDesc': 'We develop innovative solutions to meet market needs',
    
    // Contact
    'contact.title': 'Contact Us',
    'contact.subtitle': 'We are happy to receive your inquiries and suggestions',
    'contact.info': 'Contact Information',
    'contact.phone': 'Phone',
    'contact.email': 'Email',
    'contact.address': 'Address',
    'contact.addressValue': 'Saudi Arabia, Riyadh',
    'contact.sendMessage': 'Send Message',
    'contact.fullName': 'Full Name',
    'contact.subject': 'Subject',
    'contact.message': 'Your message...',
    'contact.send': 'Send Message',
    'contact.messageSent': 'Message Sent',
    'contact.messageSentDesc': 'We will contact you as soon as possible',
    'contact.error': 'Failed to send message',
    
    // How It Works
    'howitworks.title': 'How It Works?',
    'howitworks.subtitle': 'Simple steps to reach the best opportunities in the contracting sector',
    'howitworks.step1': 'Create Account',
    'howitworks.step1Desc': 'Register your account and complete your profile with official documents and specify your specialty',
    'howitworks.step2': 'Browse Tenders',
    'howitworks.step2Desc': 'Browse available tenders filtered by your specialty and field of work',
    'howitworks.step3': 'Submit Offers',
    'howitworks.step3Desc': 'Submit your offer with required files and track your application status',
    'howitworks.step4': 'Get Approval',
    'howitworks.step4Desc': 'Receive instant notification when your offer is accepted and start working',
    'howitworks.startNow': 'Start Now',
    'howitworks.faq': 'FAQ',
    'howitworks.faq1': 'Is registration free?',
    'howitworks.faq1Desc': 'Yes, registration on the platform is completely free with no hidden fees.',
    'howitworks.faq2': 'What documents are required for registration?',
    'howitworks.faq2Desc': 'Commercial register, Zakat and income certificate, and quality certificates if available.',
    'howitworks.faq3': 'How do I know about new tenders?',
    'howitworks.faq3Desc': 'You will receive notifications via email and within the platform when tenders matching your specialty are published.',
    
    // Privacy
    'privacy.title': 'Privacy Policy',
    'privacy.collectTitle': 'Information Collection',
    'privacy.collectDesc': 'We collect information you provide directly when registering on the platform, including name, email, phone number, and official documents.',
    'privacy.useTitle': 'Information Use',
    'privacy.useDesc': 'We use the collected information to provide and improve our services, and communicate with you regarding tenders and important updates.',
    'privacy.protectTitle': 'Information Protection',
    'privacy.protectDesc': 'We take appropriate security measures to protect your personal information from unauthorized access, disclosure, or modification.',
    'privacy.shareTitle': 'Information Sharing',
    'privacy.shareDesc': 'We do not sell or rent your personal information to third parties. We may share necessary information with relevant authorities upon legal request.',
    'privacy.contactTitle': 'Contact Us',
    'privacy.contactDesc': 'If you have any questions about the privacy policy, please contact us via email: privacy@kynz.com',
    
    // Terms
    'terms.title': 'Terms & Conditions',
    'terms.acceptTitle': 'Acceptance of Terms',
    'terms.acceptDesc': 'By using the KYNZ Contracting platform, you agree to comply with these terms and conditions. If you do not agree to any part of these terms, please do not use the platform.',
    'terms.registrationTitle': 'Registration and Account',
    'terms.registrationDesc': 'You must provide accurate and correct information when registering. You are responsible for maintaining the confidentiality of your account data and for all activities that occur under your account.',
    'terms.usageTitle': 'Acceptable Use',
    'terms.usageDesc': 'The platform must be used for legitimate purposes only. Using the platform for any illegal or fraudulent activities is prohibited.',
    'terms.offersTitle': 'Tenders and Offers',
    'terms.offersDesc': 'All submitted offers are binding and subject to management review. The platform reserves the right to reject any offer without stating reasons.',
    'terms.disclaimerTitle': 'Disclaimer',
    'terms.disclaimerDesc': 'The platform is not responsible for any losses or damages resulting from the use of the service or inability to use it.',
    'terms.modificationsTitle': 'Modifications',
    'terms.modificationsDesc': 'We reserve the right to modify these terms at any time. You will be notified of any substantial changes via email or notification within the platform.',
    
    // NotFound
    'notfound.title': '404',
    'notfound.message': 'Oops! Page not found',
    'notfound.backHome': 'Return to Home',
    
    // Common
    'common.search': 'Search',
    'common.manageAccount': 'Manage your account settings',
    'common.emailNotifications': 'Email Notifications',
    'common.emailNotificationsDesc': 'Receive notifications via email',
    'common.smsNotifications': 'SMS Notifications',
    'common.smsNotificationsDesc': 'Receive notifications via SMS',
    'common.newTenderAlerts': 'New Tender Alerts',
    'common.newTenderAlertsDesc': 'Alert when a tender matches your specialty',
    'common.applicationUpdates': 'Application Updates',
    'common.applicationUpdatesDesc': 'Alert when your applications are updated',
    'common.emailServerDesc': 'Email server settings for sending notifications',
    'common.usernamePlaceholder': 'username@example.com',
    'common.senderNamePlaceholder': 'Sender Name',
    'common.loading': 'Loading...',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.submitting': 'Submitting...',
    'common.selectIcon': 'Select Icon',
    'common.searchIcons': 'Search icons...',
    'common.noIconsFound': 'No icons found',
    'common.selectedIcon': 'Selected Icon',
    'common.status': 'Status',
    'common.optional': 'optional',
    'common.active': 'Active',
    'common.inactive': 'Inactive',
    'common.activate': 'Activate',
    'common.deactivate': 'Deactivate',
    'common.closed': 'Closed',
    'common.view': 'View',
    'common.edit': 'Edit',
    'common.delete': 'Delete',
    'common.create': 'Create',
    'common.update': 'Update',
    'common.actions': 'Actions',
    'common.add': 'Add',
    'common.creating': 'Creating...',
    'common.deleting': 'Deleting...',
    'common.selectOptions': 'Select options...',
    'common.clearAll': 'Clear all',
    'common.noOptionsFound': 'No options found',
    'common.remove': 'Remove',
    
    // Index Projects
    'index.projects.title': 'Latest Projects',
    'index.projects.subtitle': 'Examples of projects executed through our platform',
    'index.projects.adminTitle': 'Public Projects',
    'index.projects.adminSubtitle': 'Preview of public projects displayed on the platform',
    'index.projects.viewAllAdmin': 'View All Projects',
    'index.projects.riyadhTowers': 'Riyadh Towers Project',
    'index.projects.riyadhTowersCategory': 'Iron & Metal Structures',
    'index.projects.cementFactory': 'Modern Cement Factory',
    'index.projects.cementFactoryCategory': 'Building Materials',
    'index.projects.industrialComplex': 'Industrial Complex',
    'index.projects.industrialComplexCategory': 'Industrial Structures',
    'index.features.subtitle': 'An integrated platform combining ease of use with high professionalism',
    
    // Portfolio Projects
    'portfolio.project.riyadhTowers': 'Riyadh Towers Project',
    'portfolio.project.riyadhTowersDesc': 'Supply and installation of steel structures for commercial tower complex',
    'portfolio.project.cementFactory': 'Modern Cement Factory',
    'portfolio.project.cementFactoryDesc': 'Supply of equipment and building materials for integrated cement factory',
    'portfolio.project.industrialComplex': 'Industrial Complex',
    'portfolio.project.industrialComplexDesc': 'Design and implementation of industrial structures for multi-purpose complex',
    'portfolio.project.smartCity': 'Smart City Project',
    'portfolio.project.smartCityDesc': 'Infrastructure works for smart city project',
    'portfolio.project.warehouseComplex': 'Warehouse Complex',
    'portfolio.project.warehouseComplexDesc': 'Construction of warehouse complex with area of 50,000 square meters',
    'portfolio.project.powerStation': 'Power Station',
    'portfolio.project.powerStationDesc': 'Supply and installation of power generation station equipment',
    'portfolio.category.steel': 'Iron & Metal Structures',
    'portfolio.category.buildingMaterials': 'Building Materials',
    'portfolio.category.industrialStructures': 'Industrial Structures',
    'portfolio.category.infrastructure': 'Infrastructure',
    'portfolio.category.construction': 'Construction',
    'portfolio.category.energy': 'Energy',
    'portfolio.deleteWarning': 'Are you sure you want to delete this project? This action cannot be undone.',
    'portfolio.projectNotFoundDesc': 'We could not find this project',
    'portfolio.backToProjects': 'Back to Projects',
    'portfolio.professionalExecution': 'Professional Execution',
    'portfolio.requestSimilar': 'Request Similar Project',
    
    // Client Portfolio
    'clientPortfolio.project.kingAbdullahTower': 'King Abdullah Tower Project',
    'clientPortfolio.project.kingAbdullahTowerDesc': 'Supply of 300 tons of high-quality reinforcement steel',
    'clientPortfolio.project.oasisResidential': 'Oasis Residential Complex',
    'clientPortfolio.project.oasisResidentialDesc': 'Supply of various building materials for the project',
    'clientPortfolio.project.desalinationPlant': 'Desalination Plant',
    'clientPortfolio.project.desalinationPlantDesc': 'Supply of salt-resistant cement',
    'clientPortfolio.client.advancedConstruction': 'Advanced Construction Company',
    'clientPortfolio.client.imarGroup': 'Imar Group',
    'clientPortfolio.client.nationalWater': 'National Water Company',
    
    // Tenders Public
    'tenders.public.steelSupply': 'Steel Reinforcement Supply Tender',
    'tenders.public.paintWorks': 'Paint Supply and Installation Works Tender',
    'tenders.public.cementSupply': 'Cement Supply Tender',
    'tenders.public.metalStructures': 'Metal Structures Works Tender',
    'tenders.public.deadline': 'Final Deadline',
    'tenders.available': 'Available Tenders',
    
    // Dashboard User Name
    'dashboard.userName': 'Ahmed',
    
    
    // Sidebar Keys
    'sidebar.offers': 'Offers',
    
    // Admin Offers Keys
    'admin.offers.offerDetails': 'Offer Details',
    'admin.offers.offerInfo': 'Offer Information',
    'admin.offers.relatedTender': 'Related Tender',
    'admin.offers.proposedAmount': 'Proposed Amount',
    'admin.offers.notes': 'Notes',
    'admin.offers.submittedAt': 'Submitted At',
    'admin.offers.offerId': 'Offer ID',
    'admin.offers.clientInfo': 'Client Information',
    'admin.offers.clientStatus': 'Client Status',
    'admin.offers.quickActions': 'Quick Actions',
    'admin.offers.viewTender': 'View Tender',
    'admin.offers.viewClient': 'View Client',
    'admin.offers.accepted': 'Accepted',
    'admin.offers.rejected': 'Rejected',
    'admin.offers.pending': 'Pending',
    'admin.offers.accept': 'Accept',
    'admin.offers.reject': 'Reject',
    'admin.offers.acceptConfirm': 'Accept Offer',
    'admin.offers.acceptConfirmDesc': 'Are you sure you want to accept this offer? The client will be notified and this action cannot be undone.',
    'admin.offers.rejectConfirm': 'Reject Offer',
    'admin.offers.rejectConfirmDesc': 'Are you sure you want to reject this offer? The client will be notified and this action cannot be undone.',
    'admin.offers.acceptSuccess': 'Offer accepted successfully',
    'admin.offers.acceptSuccessDesc': 'The offer has been accepted and the client will be notified.',
    'admin.offers.rejectSuccess': 'Offer rejected successfully',
    'admin.offers.rejectSuccessDesc': 'The offer has been rejected and the client will be notified.',
    'admin.offers.acceptError': 'Failed to accept offer',
    'admin.offers.acceptErrorDesc': 'An error occurred while accepting the offer.',
    'admin.offers.rejectError': 'Failed to reject offer',
    'admin.offers.rejectErrorDesc': 'An error occurred while rejecting the offer.',
    'admin.offers.offerNotFound': 'Offer Not Found',
    'admin.offers.offerNotFoundDesc': 'The offer you are looking for does not exist or has been deleted.',
    'admin.offers.attachments': 'Attachments',
    'admin.offers.email': 'Email',
    'admin.offers.phone': 'Phone',
    'admin.offers.address': 'Address',
    
    // Admin Company Projects (English)
    'admin.companyProjects': 'Company Projects',
    'admin.companyProjectsDesc': 'Manage company projects displayed on the website',
    'admin.companyProjects.add': 'Add Project',
    'admin.companyProjects.edit': 'Edit Project',
    'admin.companyProjects.delete': 'Delete Project',
    'admin.companyProjects.noProjects': 'No projects found',
    'admin.companyProjects.created': 'Project Created',
    'admin.companyProjects.createdDesc': 'Project has been created successfully',
    'admin.companyProjects.updated': 'Project Updated',
    'admin.companyProjects.updatedDesc': 'Project has been updated successfully',
    'admin.companyProjects.deleted': 'Project Deleted',
    'admin.companyProjects.deletedDesc': 'Project has been deleted successfully',
    'admin.companyProjects.location': 'Location',
    'admin.companyProjects.completedDate': 'Completed Date',
    'admin.companyProjects.isActive': 'Active',
    'admin.companyProjects.images': 'Images',
    'admin.companyProjects.uploadImages': 'Upload Images',
    'admin.companyProjects.titleEn': 'Title (English)',
    'admin.companyProjects.titleAr': 'Title (Arabic)',
    'admin.companyProjects.descriptionEn': 'Description (English)',
    'admin.companyProjects.descriptionAr': 'Description (Arabic)',
    'admin.companyProjects.locationEn': 'Location (English)',
    'admin.companyProjects.locationAr': 'Location (Arabic)',
    
    // Admin Client Projects (English)
    'admin.clientProjects': 'Client Projects',
    'admin.clientProjectsDesc': 'Manage client projects and control their status',
    'admin.clientProjects.noProjects': 'No projects found',
    'admin.clientProjects.status': 'Status',
    'admin.clientProjects.statusPending': 'Pending',
    'admin.clientProjects.statusApproved': 'Approved',
    'admin.clientProjects.statusCompleted': 'Completed',
    'admin.clientProjects.statusRejected': 'Rejected',
    
    // Sidebar (English)
    'sidebar.companyProjects': 'Company Projects',
    'sidebar.clientProjects': 'Client Projects',
    'sidebar.adminUsers': 'Admin Users',
    
    // Common (English)
    'common.order': 'Order',
    'common.title': 'Title',
    'common.titleEn': 'Title (English)',
    'common.titleAr': 'Title (Arabic)',
    'common.descriptionEn': 'Description (English)',
    'common.descriptionAr': 'Description (Arabic)',
    'common.locationEn': 'Location (English)',
    'common.locationAr': 'Location (Arabic)',
    'common.client': 'Client',
    'common.images': 'Images',
    'common.completedDate': 'Completed Date',
    'common.allStatuses': 'All Statuses',
    'common.deleteConfirm': 'Delete Confirmation',
    
    // Sidebar Specializations
    'sidebar.specializations': 'Specializations',
    
    // Tenders Keys
    
    // Specializations Page
    'specializations.title': 'Specializations',
    'specializations.subtitle': 'Manage project specializations and categories',
    'specializations.addSpecialization': 'Add Specialization',
    'specializations.allSpecializations': 'All Specializations',
    'specializations.englishDescription': 'English Description',
    'specializations.englishDescriptionPlaceholder': 'Enter English description',
    'specializations.arabicDescriptionPlaceholder': 'Enter Arabic description',
    'specializations.actions': 'Actions',
    'specializations.id': 'ID',
    'specializations.activeDesc': 'Activate this specialization for use',
    'specializations.active': 'Active',
    'specializations.inactive': 'Inactive',
    'specializations.noDescription': 'No description',
    'specializations.noSpecializationsFound': 'No specializations found',
    'specializations.loading': 'Loading...',
    'specializations.updating': 'Updating...',
    'specializations.createButton': 'Create Specialization',
    'specializations.updateButton': 'Update Specialization',
    'specializations.cancel': 'Cancel',
    'specializations.createSuccess': 'Specialization created successfully',
    'specializations.createSuccessDesc': 'Specialization created successfully',
    'specializations.updateSuccess': 'Specialization updated successfully',
    'specializations.updateSuccessDesc': 'Specialization updated successfully',
    'specializations.deleteSuccess': 'Specialization deleted successfully',
    'specializations.deleteSuccessDesc': 'Specialization deleted successfully',
    'specializations.fetchError': 'Failed to fetch specializations',
    'specializations.createError': 'Failed to create specialization',
    'specializations.updateError': 'Failed to update specialization',
    'specializations.deleteError': 'Failed to delete specialization',
    'specializations.close': 'Close',
    'specializations.deleteSpecialization': 'Delete Specialization',
    // Validation Messages
    'specializations.englishNameRequired': 'English name is required',
    'specializations.arabicNameRequired': 'Arabic name is required',
    'specializations.englishDescriptionOptional': 'English description is optional',
    'specializations.arabicDescriptionOptional': 'Arabic description is optional',
    'specializations.listDescription': 'List of all specializations in the system',
    'specializations.name': 'Name',
    'specializations.description':'Description',
    'specializations.status':'Status',
    'specializations.created':'Created',
    'specializations.searchPlaceholder':'Search specializations',
    'specializations.editTitle':'Edit Specialization',
    'specializations.editDesc':'Edit Specialization',
    'specializations.englishName':'English Name',
    'specializations.arabicName':'Arabic Name',
    'specializations.arabicDescription':'Arabic Description',
    'specializations.createTitle':'Create Specialization',
    'specializations.createDesc':'Create Specialization',
    'specializations.updateTitle':'Update Specialization',
    'specializations.updateDesc':'Update Specialization',
    'specializations.englishNamePlaceholder':'Enter English name',
    'specializations.arabicNamePlaceholder':'Enter Arabic name',
    

    
    // Admin Settings
    'settings.adminTitle': 'Admin Settings',
    'settings.adminSubtitle': 'Manage system-wide settings and configurations',
    'settings.languageSettings': 'Language Settings',
    'settings.languageDesc': 'Set default language for platform',
    'settings.storageSettings': 'Storage Settings',
    'settings.storageDesc': 'Configure file storage and upload settings',
    'settings.emailSettings': 'Email Settings',
    'settings.emailDesc': 'Configure email sending settings',
    'settings.s3Settings': 'Amazon S3 Settings',
    'settings.s3Desc': 'Configure Amazon S3 storage settings',
    'settings.storageDisk': 'Storage Disk',
    'settings.serverStorage': 'Store inside server',
    'settings.externalStorage': 'External storage service (Amazon S3)',
    'settings.maxFileSizeKB': 'Max File Size (KB)',
    'settings.allowedFileTypes': 'Allowed File Types',
    'settings.saveStorageSettings': 'Save Storage Settings',
    'settings.saveEmailSettings': 'Save Email Settings',
    'settings.saveS3Settings': 'Save S3 Settings',
    'settings.saving': 'Saving...',
    'settings.mailerService': 'Mailer Service',
    'settings.host': 'Host',
    'settings.port': 'Port',
    'settings.encryption': 'Encryption',
    'settings.username': 'Username',
    'settings.password': 'Password',
    'settings.fromAddress': 'From Address',
    'settings.fromName': 'From Name',
    'settings.accessKeyId': 'Access Key ID',
    'settings.secretAccessKey': 'Secret Access Key',
    'settings.region': 'Region',
    'settings.bucketName': 'Bucket Name',
    'settings.url': 'URL',
    'settings.endpoint': 'Endpoint',
    'settings.usePathStyleEndpoint': 'Use Path Style Endpoint',
    'settings.storageUpdated': 'Storage settings updated',
    'settings.storageUpdatedDesc': 'Storage settings have been updated successfully',
    'settings.emailUpdated': 'Email settings updated',
    'settings.emailUpdatedDesc': 'Email settings have been updated successfully',
    'settings.s3Updated': 'S3 settings updated',
    'settings.s3UpdatedDesc': 'S3 settings have been updated successfully',
    'settings.storageUpdateError': 'Failed to update storage settings',
    'settings.emailUpdateError': 'Failed to update email settings',
    'settings.s3UpdateError': 'Failed to update S3 settings',
    
    // Account Status
    'accountStatus.pending.title': 'Account Under Review',
    'accountStatus.pending.description': 'Your account is currently under review. You can view your data but cannot make changes until approval.',
    'accountStatus.pending.canDo': 'You can view existing projects, tenders, and history.',
    'accountStatus.pending.restoreAccess': 'Please wait for admin approval. Check your email for updates.',
    'accountStatus.pending.welcome': 'Account Under Review',
    'accountStatus.pending.welcomeMessage': 'Your account is currently under review. You can view your data but cannot make changes until approval.',
    
    'accountStatus.suspended.title': 'Account Temporarily Suspended',
    'accountStatus.suspended.description': 'Your account has been temporarily suspended. You can view your data but cannot make changes.',
    'accountStatus.suspended.canDo': 'You can view your data and contact support for assistance.',
    'accountStatus.suspended.restoreAccess': 'Contact support to resolve suspension.',
    'accountStatus.suspended.welcome': 'Account Suspended',
    'accountStatus.suspended.welcomeMessage': 'Your account has been temporarily suspended. You can view your data but cannot make changes.',
    
    'accountStatus.rejected.title': 'Account Rejected',
    'accountStatus.rejected.description': 'Your account has been permanently rejected. You can only view and export your data.',
    'accountStatus.rejected.canDo': 'You can view your data and request data export.',
    'accountStatus.rejected.restoreAccess': 'Contact admin if you believe this is an error.',
    'accountStatus.rejected.welcome': 'Account Rejected',
    'accountStatus.rejected.welcomeMessage': 'Your account has been permanently rejected. You can only view and export your data.',
    
    'accountStatus.default.title': 'Account Status',
    'accountStatus.default.description': 'Your account status is not active.',
    'accountStatus.default.canDo': 'You can view your existing data.',
    'accountStatus.default.restoreAccess': 'Contact support for assistance.',
    
    'accountStatus.reason': 'Reason',
    'accountStatus.date': 'Date',
    'accountStatus.whatYouCanDo': 'What you can do',
    'accountStatus.restoreAccess': 'How to restore access',
    'accountStatus.contactSupport': 'Contact Support',
    
    // Read Only Mode
    'readOnly.pending': 'Action unavailable while account is under review',
    'readOnly.suspended': 'Action unavailable while account is suspended',
    'readOnly.rejected': 'Action unavailable for rejected accounts',
    'readOnly.default': 'Action unavailable in current account status',
    
  },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    // Get from localStorage or default to 'ar'
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('language') as Language;
      return saved === 'ar' || saved === 'en' ? saved : 'ar';
    }
    return 'ar';
  });

  useEffect(() => {
    // Save to localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('language', language);
      // Update document direction
      document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const isRTL = language === 'ar';

  const value: LanguageContextType = {
    language,
    setLanguage,
    isRTL,
    t,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
